import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { calculateLeadTime, COUNTRIES, INDIAN_STATES, isEuropeanCountry } from '../utils/helpers';
import FileUpload from '../components/FileUpload';
import { useAuth } from '../contexts/AuthContext';
import PriceBreakdown from '../components/PriceBreakdown';
import FeedbackModal from '../components/FeedbackModal';
function InvoicePage() {
    const { id } = useParams(); // Get invoice ID from URL if editing
    const isEditMode = Boolean(id);
    const { user, hasRole } = useAuth(); // Get user info for role-based buttons
    const MAX_ALLOWED_DATE = '2050-12-31';

    const clampDateToMax = (dateValue) => {
        if (!dateValue) return '';
        return dateValue > MAX_ALLOWED_DATE ? MAX_ALLOWED_DATE : dateValue;
    };

    const normalizeDateForInput = (value) => {
        if (!value) return '';
        const v = value.toString().trim();
        // Accept native YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
            const y = parseInt(v.split('-')[0], 10);
            if (y < 2024) return '';
            return clampDateToMax(v);
        }
        // Accept DD-MM-YY or DD/MM/YY or DD-MM-YYYY and convert
        const m = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
        if (m) {
            let day = String(m[1]).padStart(2, '0');
            let month = String(m[2]).padStart(2, '0');
            let year = m[3];
            if (year.length === 2) {
                // Map 2-digit years to 2000-2099
                year = String(2000 + parseInt(year, 10));
            }
            const iso = `${year}-${month}-${day}`;
            const yNum = parseInt(year, 10);
            if (yNum < 2024) return '';
            const dt = new Date(iso);
            if (!Number.isNaN(dt.getTime())) return clampDateToMax(iso);
            return '';
        }
        // Fallback: try Date parse
        const dt = new Date(v);
        if (!Number.isNaN(dt.getTime())) {
            const iso = dt.toISOString().split('T')[0];
            const y = parseInt(iso.split('-')[0], 10);
            if (y < 2024) return '';
            return clampDateToMax(iso);
        }
        return '';
    };

    const toInputDate = (dateValue) => {
        if (!dateValue) return '';
        // Use normalizeDateForInput to ensure consistent behavior and year checks
        return normalizeDateForInput(dateValue);
    };

    const initialFormData = {
        // Customer Info
        customerName: '',
        customerPoNumber: '',
        crmOpportunityLink: '',
        piInvoiceReference: '',
        poDate: '',
        poValue: '',

        // Ship-To Address
        shipToAddress: {
            contactName: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            country: '',
            zipCode: '',
            iecNumber: '',
            iecBranch: '',
            gst: '',
            adCode: '',
            vatId: '',
            eoriNumber: ''
        },

        // Bill-To Address
        billToAddress: {
            contactName: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            country: '',
            zipCode: '',
            iecNumber: '',
            iecBranch: '',
            gst: '',
            adCode: '',
            vatId: '',
            eoriNumber: ''
        },

        // Products array (multiple products support)
        products: [],

        // Contact & Delivery
        customerRequestedDeliveryDate: '',
        receiverContactName: '',
        receiverContactNumber: '',
        receiverEmailId: '',
        mapToDifferentAccount: false,
        customerAccountEmailId: '',

        // Shipping
        useShippingAccount: false,
        shippingAccountNumber: '',
        courierName: '',
        shippingCost: '',
        incoterms: '',

        // Management
        productManagerName: '',
        paymentMode: '',
        paymentReceived: '',
        paymentReceivedDate: '',
        bdName: '',
        typeOfOrder: '',
        numberOfSOs: '',
orderDurationMonths: '',
orderDurationDays: '',
soSchedule: [],

        // New Phase 3 field
        purchaseLocation: '',

        // Common Product Requirements (Moved from per-product)
        productStatus: '',
                process: '',

        csrCategory: [],
        customerSpecificRequirement: ''
    };

    const [formData, setFormData] = useState(initialFormData);


    //feedback 
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
const [feedbackLoading, setFeedbackLoading] = useState(false);
    // Read-only mode when invoice is completed
    const isReadOnly = formData.status === 'completed';

    // Files selected during create mode (deferred upload)
    const [selectedFiles, setSelectedFiles] = useState([]);

    // Current product being added
    const [currentProduct, setCurrentProduct] = useState({
        productCode: '',
        quantity: '',
        calculatedLeadTime: '',
        unitPrice: ''
    });

    // CSR Category options
    const CSR_CATEGORY_OPTIONS = [
        'Custom Lens',
        'Without Lens Gluing',
        'With Lens Gluing',
        'Cable',
        'Custom Firmware',
        'Custom Lens Holder',
        'Single Packing',
        'Bulk Packing',
        'Other'
    ];

    // Payment Mode options (Phase 3 Updates)
    const PAYMENT_MODE_OPTIONS = [
        'Paypal',
        'PaypalLLC-USA',
        'Wire transfer',
        'Stripe',
        'None'
    ];

    // Payment Received options
    const PAYMENT_RECEIVED_OPTIONS = [
        'Payment Not Received',
        'Part Payment Received',
        'Net 90 Days',
        'Net 75 Days',
        'NET 60 DAYS',
        'Net 30Days',
        'Net 15 Days',
        'Advance',
        '50% Advance / 50% Net 30',
        '50 % Advance / 50 % Before Delivery'
    ];

    // Purchase Location Options
    const PURCHASE_LOCATION_OPTIONS = [
        'India',
        'e-conLLC(USA)'
    ];

    // Product Manager Options
    const PRODUCT_MANAGER_OPTIONS = [
        'Suresh Madhu',
        'Ranjith Kumar',
        'Gomathi Sankar',
        'Ram Prasad',
        'Dilip Kumar',
        'Balaji',
        'Vinoth R'

    ];

    // Product search
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [csrDropdownOpen, setCsrDropdownOpen] = useState(false);
    const csrDropdownRef = useRef(null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // UI state
    const [loading, setLoading] = useState(false);
    const [workflowLoading, setWorkflowLoading] = useState(false);
    const [attachmentCount, setAttachmentCount] = useState(0);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    // PO check state
    const [poChecking, setPoChecking] = useState(false);
    const [poExists, setPoExists] = useState(false);
    const [poCheckMessage, setPoCheckMessage] = useState(null);

    // Auto-hide PO check message after 5 seconds
    useEffect(() => {
        if (poCheckMessage && !poChecking) {
            const timer = setTimeout(() => {
                setPoCheckMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [poCheckMessage, poChecking]);

    const [isTableView, setIsTableView] = useState(localStorage.getItem('invoiceViewMode') !== 'card');

    // Free Sample dialog state (handles both mixing and price confirmation)
    const [mixingDialog, setMixingDialog] = useState({
        isOpen: false,
        dialogType: null, // 'mixing' or 'zero-price'
        existingMode: null, // 'free' or 'paid' (for mixing)
        newMode: null, // 'free' or 'paid' (for mixing)
        productToAdd: null
    });


    //massorder so change

const handleNumberOfSOsChange = (e) => {
    const count = parseInt(e.target.value, 10) || 0;

    // Initialize billing sync state for all SOs
    const newSoBillingSync = {};
    for (let i = 0; i < count; i++) {
        newSoBillingSync[i] = false;
    }
    setSoBillingSameAsShipping(newSoBillingSync);

    setFormData(prev => {
        const currentSchedule = prev.soSchedule || [];
        const newSchedule = [];

        // Preserve existing SOs and add new ones if count increased
        for (let index = 0; index < count; index++) {
            if (currentSchedule[index]) {
                // Preserve existing SO with all its data
                newSchedule.push(currentSchedule[index]);
            } else {
                // Create new SO
                const soObj = {
                    soNumber: index + 1,
                    quantity: '',
                    deliveryDate: '',
                    crmSoNumber: null,
                    productQuantities: {},
                        shippingCharge: null

                };

                // Initialize productQuantities for all currently selected products (for Mass Order)
                if (prev.typeOfOrder === 'Mass Order' && prev.products) {
                    prev.products.forEach(product => {
                        soObj.productQuantities[product.productCode] = '';
                    });
                }

                // SO 1: Initialize with empty address/contact structures
                if (index === 0) {
                    soObj.shippingAddress = {
                        contactName: '',
                        address1: '',
                        address2: '',
                        city: '',
                        state: '',
                        country: '',
                        zipCode: '',
                        iecNumber: '',
                        iecBranch: '',
                        gst: '',
                        adCode: '',
                        vatId: '',
                        eoriNumber: ''
                    };
                    soObj.billingAddress = {
                        contactName: '',
                        address1: '',
                        address2: '',
                        city: '',
                        state: '',
                        country: '',
                        zipCode: '',
                        iecNumber: '',
                        iecBranch: '',
                        gst: '',
                        adCode: '',
                        vatId: '',
                        eoriNumber: ''
                    };
                    soObj.receiverContact = {
                        name: '',
                        number: '',
                        email: ''
                    };
                    soObj.mapToDifferentAccount = false;
                    soObj.customerAccountEmailId = '';
                }
                // SO 2+: Default to sameAsFirst = true
                else {
                    soObj.sameAsFirst = true;

                    // Get SO 1 data to copy if it exists and has data
                    const so1 = newSchedule[0];
                    const so1HasData = so1 && (
                        (so1.shippingAddress && (so1.shippingAddress.address1 || so1.shippingAddress.city || so1.shippingAddress.country)) ||
                        (so1.billingAddress && (so1.billingAddress.address1 || so1.billingAddress.city || so1.billingAddress.country)) ||
                        (so1.receiverContact && (so1.receiverContact.name || so1.receiverContact.number || so1.receiverContact.email)) ||
                        so1.mapToDifferentAccount ||
                        so1.customerAccountEmailId
                    );

                    if (so1HasData && so1.sameAsFirst !== false) {
                        // Copy SO1 data
                        soObj.shippingAddress = { ...so1.shippingAddress };
                        soObj.billingAddress = { ...so1.billingAddress };
                        soObj.receiverContact = { ...so1.receiverContact };
                        soObj.mapToDifferentAccount = so1.mapToDifferentAccount || false;
                        soObj.customerAccountEmailId = so1.customerAccountEmailId || '';
                    } else {
                        // Initialize with empty structures
                        soObj.shippingAddress = {
                            contactName: '',
                            address1: '',
                            address2: '',
                            city: '',
                            state: '',
                            country: '',
                            zipCode: '',
                            iecNumber: '',
                            iecBranch: '',
                            gst: '',
                            adCode: '',
                            vatId: '',
                            eoriNumber: ''
                        };
                        soObj.billingAddress = {
                            contactName: '',
                            address1: '',
                            address2: '',
                            city: '',
                            state: '',
                            country: '',
                            zipCode: '',
                            iecNumber: '',
                            iecBranch: '',
                            gst: '',
                            adCode: '',
                            vatId: '',
                            eoriNumber: ''
                        };
                        soObj.receiverContact = {
                            name: '',
                            number: '',
                            email: ''
                        };
                        soObj.mapToDifferentAccount = false;
                        soObj.customerAccountEmailId = '';
                    }
                }

                newSchedule.push(soObj);
            }
        }

        return {
            ...prev,
            numberOfSOs: e.target.value,
            soSchedule: newSchedule
        };
    });
};

// Handle "Same as SO 1" checkbox toggle for SO 2+
const handleSameAsFirstToggle = (soIndex, isChecked) => {
    setFormData(prev => {
        const updatedSoSchedule = [...prev.soSchedule];
        updatedSoSchedule[soIndex].sameAsFirst = isChecked;

        if (!isChecked) {
            // Create empty per-SO address/contact structures when unchecked
            updatedSoSchedule[soIndex].shippingAddress = {
                contactName: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                country: '',
                zipCode: '',
                iecNumber: '',
                iecBranch: '',
                gst: '',
                adCode: '',
                vatId: '',
                eoriNumber: ''
            };
            updatedSoSchedule[soIndex].billingAddress = {
                contactName: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                country: '',
                zipCode: '',
                iecNumber: '',
                iecBranch: '',
                gst: '',
                adCode: '',
                vatId: '',
                eoriNumber: ''
            };
            updatedSoSchedule[soIndex].receiverContact = {
                name: '',
                number: '',
                email: ''
            };
            updatedSoSchedule[soIndex].mapToDifferentAccount = false;
            updatedSoSchedule[soIndex].customerAccountEmailId = '';
            // Reset billing sync for this SO
            setSoBillingSameAsShipping(prev => ({
                ...prev,
                [soIndex]: false
            }));
        } else {
            // If checked, copy SO1's data into this SO (so it has complete data to submit)
            const so1 = updatedSoSchedule[0];
            if (so1) {
                updatedSoSchedule[soIndex].shippingAddress = { ...so1.shippingAddress };
                updatedSoSchedule[soIndex].billingAddress = { ...so1.billingAddress };
                updatedSoSchedule[soIndex].receiverContact = { ...so1.receiverContact };
                updatedSoSchedule[soIndex].mapToDifferentAccount = so1.mapToDifferentAccount || false;
                updatedSoSchedule[soIndex].customerAccountEmailId = so1.customerAccountEmailId || '';
            }
            // Reset billing sync for this SO when copying from SO1
            setSoBillingSameAsShipping(prev => ({
                ...prev,
                [soIndex]: false
            }));
        }

        return {
            ...prev,
            soSchedule: updatedSoSchedule
        };
    });
};

// Update per-SO address fields
const handleSOAddressChange = (soIndex, addressType, field, value) => {
    setFormData(prev => {
        const updatedSoSchedule = [...prev.soSchedule];
        if (!updatedSoSchedule[soIndex][addressType]) {
            updatedSoSchedule[soIndex][addressType] = {};
        }
        updatedSoSchedule[soIndex][addressType][field] = value;

        // If updating shipping and billing is synced for this SO, sync to billing
        if (addressType === 'shippingAddress' && soBillingSameAsShipping[soIndex]) {
            updatedSoSchedule[soIndex].billingAddress = { ...updatedSoSchedule[soIndex].shippingAddress };
        }

        // If SO 1 is being changed, propagate to SO 2+ where sameAsFirst === true
        if (soIndex === 0) {
            for (let i = 1; i < updatedSoSchedule.length; i++) {
                if (updatedSoSchedule[i].sameAsFirst === true) {
                    if (!updatedSoSchedule[i][addressType]) {
                        updatedSoSchedule[i][addressType] = {};
                    }
                    updatedSoSchedule[i][addressType][field] = value;
                    // Also sync billing if it's synced for that SO
                    if (addressType === 'shippingAddress' && soBillingSameAsShipping[i]) {
                        updatedSoSchedule[i].billingAddress = { ...updatedSoSchedule[i].shippingAddress };
                    }
                }
            }
        }

        return {
            ...prev,
            soSchedule: updatedSoSchedule
        };
    });
};

// Update per-SO receiver contact fields
const handleSOReceiverContactChange = (soIndex, field, value) => {
    setFormData(prev => {
        const updatedSoSchedule = [...prev.soSchedule];
        if (!updatedSoSchedule[soIndex].receiverContact) {
            updatedSoSchedule[soIndex].receiverContact = { name: '', number: '', email: '' };
        }
        updatedSoSchedule[soIndex].receiverContact[field] = value;

        // If SO 1 receiver contact is being changed, propagate to SO 2+ where sameAsFirst === true
        if (soIndex === 0) {
            for (let i = 1; i < updatedSoSchedule.length; i++) {
                if (updatedSoSchedule[i].sameAsFirst === true) {
                    if (!updatedSoSchedule[i].receiverContact) {
                        updatedSoSchedule[i].receiverContact = { name: '', number: '', email: '' };
                    }
                    updatedSoSchedule[i].receiverContact[field] = value;
                }
            }
        }

        return {
            ...prev,
            soSchedule: updatedSoSchedule
        };
    });
};

// Update per-SO account mapping fields
const handleSOAccountMappingChange = (soIndex, field, value) => {
    setFormData(prev => {
        const updatedSoSchedule = [...prev.soSchedule];
        updatedSoSchedule[soIndex][field] = value;

        // If SO 1 account mapping is being changed, propagate to SO 2+ where sameAsFirst === true
        if (soIndex === 0) {
            for (let i = 1; i < updatedSoSchedule.length; i++) {
                if (updatedSoSchedule[i].sameAsFirst === true) {
                    updatedSoSchedule[i][field] = value;
                }
            }
        }

        return {
            ...prev,
            soSchedule: updatedSoSchedule
        };
    });
};

// Update per-SO product quantity (Mass Order only)
const handleSOProductQuantityChange = (soIndex, productCode, value) => {
    setFormData(prev => {
        const updatedSoSchedule = [...prev.soSchedule];
        if (!updatedSoSchedule[soIndex].productQuantities) {
            updatedSoSchedule[soIndex].productQuantities = {};
        }
        updatedSoSchedule[soIndex].productQuantities[productCode] = value === '' ? '' : parseInt(value, 10);

        // Recalculate SO total quantity from product quantities
        const soTotal = Object.values(updatedSoSchedule[soIndex].productQuantities).reduce(
            (sum, qty) => sum + (Number(qty) || 0),
            0
        );
        updatedSoSchedule[soIndex].quantity = soTotal;

        return {
            ...prev,
            soSchedule: updatedSoSchedule
        };
    });
};

const handleSOShippingChargeChange = (soIndex, value) => {
    setFormData(prev => {
        const updatedSoSchedule = [...prev.soSchedule];

        updatedSoSchedule[soIndex] = {
            ...updatedSoSchedule[soIndex],
            shippingCharge: value === '' ? '' : Number(value)
        };

        return {
            ...prev,
            soSchedule: updatedSoSchedule
        };
    });
};
    // Helper: Check if existing products are Free Sample or Paid
    const getExistingProductMode = () => {
        if (formData.products.length === 0) return null;
        const firstProduct = formData.products[0];
        return firstProduct.unitPrice === 0 || Number(firstProduct.unitPrice) === 0 ? 'free' : 'paid';
    };

    // Helper: Determine if new product would be Free Sample or Paid
    const getNewProductMode = (unitPrice) => {
        return unitPrice === 0 || Number(unitPrice) === 0 ? 'free' : 'paid';
    };

    // Auto-set Shipping Cost to $0 when typeOfOrder is Free Sample
    useEffect(() => {
        if (formData.typeOfOrder === 'Free Sample' && formData.shippingCost !== '0' && formData.shippingCost !== 0) {
            setFormData(prev => ({ ...prev, shippingCost: '0' }));
        }
    }, [formData.typeOfOrder]);

    // Handle dialog: Add product (for either mixing or zero-price confirmation)
    const handleConfirmAddProduct = (newProduct, changeTypeToFreeSample = false) => {
        if (changeTypeToFreeSample) {
            setFormData(prev => ({
                ...prev,
                typeOfOrder: 'Free Sample',
                products: [...prev.products, newProduct],
                soSchedule: prev.typeOfOrder === 'Mass Order' ? prev.soSchedule.map(so => ({
                    ...so,
                    productQuantities: { ...so.productQuantities, [newProduct.productCode]: '' }
                })) : prev.soSchedule
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                products: [...prev.products, newProduct],
                soSchedule: prev.typeOfOrder === 'Mass Order' ? prev.soSchedule.map(so => ({
                    ...so,
                    productQuantities: { ...so.productQuantities, [newProduct.productCode]: '' }
                })) : prev.soSchedule
            }));
        }

        // Reset current product form
        setCurrentProduct({
            productCode: '',
            quantity: '',
            calculatedLeadTime: '',
            unitPrice: ''
        });
        setSelectedProduct(null);
        setSearchTerm('');
        setMixingDialog({ isOpen: false, dialogType: null, existingMode: null, newMode: null, productToAdd: null });
    };

    // Handle dialog: Remove existing products and proceed (mixing case)
    const handleRemoveExistingAndProceed = (newProduct) => {
        setFormData(prev => ({
            ...prev,
            products: [newProduct]
        }));
        
        // Reset current product form
        setCurrentProduct({
            productCode: '',
            quantity: '',
            calculatedLeadTime: '',
            unitPrice: ''
        });
        setSelectedProduct(null);
        setSearchTerm('');
        setMixingDialog({ isOpen: false, dialogType: null, existingMode: null, newMode: null, productToAdd: null });
    };

    // Handle dialog: Cancel any pending action
    const handleCancelDialog = () => {
        setMixingDialog({ isOpen: false, dialogType: null, existingMode: null, newMode: null, productToAdd: null });
    };

    // Helper: Check if any product is a "Contact Us" item (lead time 9999)
    const hasContactUsProduct = formData.products.some(
        p => p.calculatedLeadTime === 9999
    );

    // Computed: check if all products have lead time
    const hasNonStandardCSR = formData.csrCategory && formData.csrCategory.some(cat =>
        cat !== 'Single Packing' && cat !== 'Bulk Packing'
    );

    const hasCompleteShippingAccount = formData.useShippingAccount &&
        String(formData.shippingAccountNumber || '').trim() !== '' &&
        String(formData.courierName || '').trim() !== '';
    const hasZeroShippingCost = formData.typeOfOrder === 'Free Sample' || hasCompleteShippingAccount;
    const effectiveShippingCost = hasZeroShippingCost ? 0 : (Number(formData.shippingCost) || 0);

// Shipping Charge Calculation
const getMassOrderShippingCharge = (soIndex) => {
    if (formData.typeOfOrder !== 'Mass Order') return 0;

    const soQuantities = formData.soSchedule.map(so =>
        Object.values(so.productQuantities || {}).reduce(
            (sum, qty) => sum + (Number(qty) || 0),
            0
        )
    );

    const totalQuantity = soQuantities.reduce((sum, qty) => sum + qty, 0);

    if (totalQuantity === 0) return 0;

    return effectiveShippingCost * (soQuantities[soIndex] / totalQuantity);
};


    const [billingSameAsShipping, setBillingSameAsShipping] = useState(false);
    const [soBillingSameAsShipping, setSoBillingSameAsShipping] = useState({});

    // Load invoice data if in edit mode, reset form if in create mode
    useEffect(() => {
        if (isEditMode) {
            loadInvoiceData();
        } else {
            // Reset form when navigating to create mode
            setFormData(initialFormData);
            setSelectedFiles([]);
            setBillingSameAsShipping(false);
            setError(null);
            setSuccess(null);
        }
    }, [id]);

    // Close CSR dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (csrDropdownRef.current && !csrDropdownRef.current.contains(event.target)) {
                setCsrDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadInvoiceData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`http://localhost:5000/api/invoices/${id}`);
            const invoice = response.data;

console.log('CreatedBy:', invoice.createdBy?.email);
console.log('StoredCreatedByEmail:', invoice.storedCreatedByEmail);
console.log('CreatedByEmail:', invoice.createdByEmail);


            // Pre-populate form with invoice data
            setFormData({
                customerName: invoice.customerName || '',
                customerPoNumber: invoice.customerPoNumber || '',
                                 crmOpportunityLink: invoice.crmOpportunityLink || '',
                soNumber: invoice.soNumber || '',
                piInvoiceReference: invoice.piInvoiceReference || '',
                poDate: toInputDate(invoice.poDate),
                poValue: invoice.poValue || '',
                shipToAddress: {
                    contactName: invoice.shipToAddress?.contactName || '', // Use contactName from invoice if available
                    address1: '', address2: '', city: '', state: '', country: '', zipCode: '',
                    iecNumber: '', iecBranch: '', gst: '', adCode: '', vatId: '', eoriNumber: '',
                    ...(invoice.shipToAddress || {})
                },
                billToAddress: {
                    contactName: invoice.billToAddress?.contactName || '', // Use contactName from invoice if available
                    address1: '', address2: '', city: '', state: '', country: '', zipCode: '',
                    iecNumber: '', iecBranch: '', gst: '', adCode: '', vatId: '', eoriNumber: '',
                    ...(invoice.billToAddress || {})
                },
                products: invoice.products || [],
                customerRequestedDeliveryDate: toInputDate(invoice.customerRequestedDeliveryDate),
                receiverContactName: invoice.receiverContactName || '',
                receiverContactNumber: invoice.receiverContactNumber || '',
                receiverEmailId: invoice.receiverEmailId || '',
                mapToDifferentAccount: invoice.mapToDifferentAccount || false,
                customerAccountEmailId: invoice.customerAccountEmailId || '',
                useShippingAccount: invoice.useShippingAccount || false,
                shippingAccountNumber: invoice.shippingAccountNumber || '',
                courierName: invoice.courierName || '',
                shippingCost: invoice.shippingCost || '',
                incoterms: invoice.incoterms || '',
                productManagerName: invoice.productManagerName || '',
                paymentMode: PAYMENT_MODE_OPTIONS.find(o => o.toLowerCase() === (invoice.paymentMode || '').toLowerCase()) || invoice.paymentMode || '',
                paymentReceived: PAYMENT_RECEIVED_OPTIONS.find(o => o.toLowerCase() === (invoice.paymentReceived || '').toLowerCase()) || invoice.paymentReceived || '',
                paymentReceivedDate: toInputDate(invoice.paymentReceivedDate),
                bdName: invoice.bdName || (user ? user.name : ''),
                typeOfOrder: invoice.typeOfOrder || '',
                  numberOfSOs: invoice.numberOfSOs || '',
orderDurationMonths: invoice.orderDurationMonths || '',
orderDurationDays: invoice.orderDurationDays || '',
soSchedule: invoice.soSchedule || [],
                status: invoice.status || 'draft',
                createdBy: invoice.createdBy || null,
                storedCreatedByEmail: invoice.storedCreatedByEmail || '',
                createdByEmail: invoice.createdByEmail || '',
                purchaseLocation: invoice.purchaseLocation || '',
                productStatus: invoice.productStatus || '',
                                process: invoice.process || '',

                csrCategory: invoice.csrCategory || [],
                customerSpecificRequirement: invoice.customerSpecificRequirement || ''
            });

            setLoading(false);
        } catch (err) {
            console.error('Error loading invoice:', err);
            setError('Failed to load Sales Order data');
            setLoading(false);
        }
    };

    // Search products
    useEffect(() => {
        const searchProducts = async () => {
            if (searchTerm.length >= 3) {
                try {
                    const response = await axios.get(`http://localhost:5000/api/products/search?q=${searchTerm}`);
                    setSearchResults(response.data);
                    setShowDropdown(true);
                } catch (err) {
                    console.error('Error searching products:', err);
                }
            } else {
                setSearchResults([]);
                setShowDropdown(false);
            }
        };

        const debounce = setTimeout(searchProducts, 300);
        return () => clearTimeout(debounce);
    }, [searchTerm]);

    useEffect(() => {
        if (selectedProduct && currentProduct.quantity) {
            const leadTime = calculateLeadTime(parseInt(currentProduct.quantity), selectedProduct);
            setCurrentProduct(prev => ({
                ...prev,
                calculatedLeadTime: leadTime // Store as is (number, 'MANUAL', or 'INVALID')
            }));
        }
    }, [currentProduct.quantity, selectedProduct]);

    // Calculate and display max lead time info
    // No CRD auto-calculation - only track max lead time for reference
    useEffect(() => {
        const productsWithLeadTime = formData.products.filter(
            p => typeof p.calculatedLeadTime === 'number' && p.calculatedLeadTime !== 9999
        );
        const hasPendingPMProduct = formData.products.length > 0 && productsWithLeadTime.length < formData.products.length;

        if (hasPendingPMProduct || hasContactUsProduct) {
            // At least one product is pending PM or contact-us - mark status as pending
            setFormData(prev => ({
                ...prev,
                customerRequestedDeliveryDate: 'Pending PM'
            }));
        } else if (formData.products.length === 0) {
            // No products - clear status
            setFormData(prev => ({
                ...prev,
                customerRequestedDeliveryDate: ''
            }));
        }
        // Once all products have lead times, CRD is now fully manual (user input only)
    }, [formData.products, formData.csrCategory]);

    const toggleViewMode = (e) => {
        const newValue = e.target.checked;
        setIsTableView(newValue);
        localStorage.setItem('invoiceViewMode', newValue ? 'table' : 'card');
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let normalizedValue = type === 'date' ? normalizeDateForInput(value) : value;

        // Field-specific sanitization
        if (name === 'receiverContactNumber') {
            // Allow digits only
            normalizedValue = (normalizedValue || '').toString().replace(/\D+/g, '');
        }
        if (name === 'shippingCost') {
            // Enforce integer-only shipping cost (no fractions)
            normalizedValue = (normalizedValue || '').toString().replace(/[^0-9]/g, '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : normalizedValue
        }));

        // Clear PO check state when user edits the PO field
        if (name === 'customerPoNumber') {
            setPoExists(false);
            setPoCheckMessage(null);
        }
    };

    // Check PO availability via backend endpoint
    const checkPoAvailability = async (po) => {
        if (!po || !po.toString().trim()) {
            setPoExists(false);
            setPoCheckMessage(null);
            return false;
        }

        setPoChecking(true);
        setPoCheckMessage('Checking...');
        try {
            const url = `http://localhost:5000/api/invoices/check-po?po=${encodeURIComponent(po)}${isEditMode ? `&excludeId=${id}` : ''}`;
            const res = await axios.get(url);
            if (res.data && res.data.exists) {
                setPoExists(true);
                setPoCheckMessage(res.data.customerPoNumber ? `Already used by invoice ${res.data.invoiceId}` : `Already used`);
                return true;
            } else {
                setPoExists(false);
                setPoCheckMessage('New PO Number');
                return false;
            }
        } catch (err) {
            console.error('PO check error:', err);
            setPoExists(false);
            setPoCheckMessage('Unable to verify PO');
            return false;
        } finally {
            setPoChecking(false);
        }
    };

    // Auto-fill BD Name with logged-in user's name if not set (mostly for new invoices)
    useEffect(() => {
        if (!isEditMode && user && !formData.bdName) {
            setFormData(prev => ({ ...prev, bdName: user.name }));
        }
    }, [user, isEditMode]);

    const handleAddressChange = (addressType, field, value) => {
        setFormData(prev => {
            const newAddress = {
                ...prev[addressType],
                [field]: value
            };

            const newState = {
                ...prev,
                [addressType]: newAddress
            };

            // If we're updating shipping and billing is synced, update billing too
            if (addressType === 'shipToAddress' && billingSameAsShipping) {
                newState.billToAddress = { ...newAddress };
            }

            return newState;
        });
    };

    const handleBillingSameAsShipping = (checked) => {
        setBillingSameAsShipping(checked);
        if (checked) {
            setFormData(prev => ({
                ...prev,
                billToAddress: { ...prev.shipToAddress }
            }));
        }
    };

    const handleSOBillingSameAsShipping = (soIndex, checked) => {
        setSoBillingSameAsShipping(prev => ({
            ...prev,
            [soIndex]: checked
        }));
        if (checked) {
            setFormData(prev => {
                const updatedSoSchedule = [...prev.soSchedule];
                updatedSoSchedule[soIndex].billingAddress = { ...updatedSoSchedule[soIndex].shippingAddress };

                // If SO 1 is being synced, propagate billing address to SO 2+ where sameAsFirst === true
                if (soIndex === 0) {
                    for (let i = 1; i < updatedSoSchedule.length; i++) {
                        if (updatedSoSchedule[i].sameAsFirst === true) {
                            updatedSoSchedule[i].billingAddress = { ...updatedSoSchedule[0].shippingAddress };
                        }
                    }
                }

                return {
                    ...prev,
                    soSchedule: updatedSoSchedule
                };
            });
        }
    };

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        setSearchTerm(`${product.productCode} - ${product.productName}`);
        setCurrentProduct(prev => ({
            ...prev,
            productCode: product.productCode
        }));
        setSearchResults([]);
        setShowDropdown(false);
    };

    const handleAddProduct = () => {
        if (!selectedProduct) {
            alert('Please select a product');
            return;
        }
        if (!currentProduct.quantity || currentProduct.quantity <= 0) {
            alert('Please enter a valid quantity');
            return;
        }

        const qty = parseInt(currentProduct.quantity);
        const T1 = parseInt(selectedProduct.threshold1) || 0;
        const T2 = parseInt(selectedProduct.threshold2) || 0;
        const T3 = parseInt(selectedProduct.threshold3) || 0;

        // Requirement: 1 to T1 (Flexible Range)
        // Requirement: T2 only (Fixed Value) - if T2 > 0
        // Requirement: T3 only (Fixed Value) - if T3 > 0
        // Requirement: > Highest Threshold (Manual Calculation)

        const maxThreshold = T3 > 0 ? T3 : (T2 > 0 ? T2 : T1);
        const isValidQty = (qty <= T1) ||
            (T2 > 0 && qty === T2) ||
            (T3 > 0 && qty === T3) ||
            (qty > maxThreshold);

        if (!isValidQty) {
            let errorMsg = `Unable to order this quantity.\n\nFor this product, allowed quantities are:\n- 1 to ${T1}`;
            if (T2 > 0) errorMsg += `\n- Exactly ${T2}`;
            if (T3 > 0) errorMsg += `\n- Exactly ${T3}`;
            errorMsg += `\n- Greater than ${maxThreshold} (Manual PM Processing)`;

            alert(errorMsg);
            return;
        }

        // Determine unit price - use entered price or default, never force based on typeOfOrder
        const enteredPrice = currentProduct.unitPrice !== '' ? currentProduct.unitPrice : null;
        const effectivePrice = enteredPrice !== null ? Number(enteredPrice) : (selectedProduct.price || null);

        // Only block if price is completely missing (null/undefined)
        // $0 price is allowed here — form-submit validates coherence with Type of Order
        if ((effectivePrice === null || effectivePrice === undefined)) {
            alert('This product has no default price. Please enter a unit price.');
            return;
        }

        const newProduct = {
            ...currentProduct,
            productName: selectedProduct.productName,
            price: selectedProduct.price,
            unitPrice: enteredPrice !== null ? Number(enteredPrice) : selectedProduct.price,
            availableQuantity: selectedProduct.availableQuantity,
            // Store catalog lead time data so lead times can be recalculated if CSR changes later
            leadTime: selectedProduct.leadTime,
            threshold1: selectedProduct.threshold1,
            leadTime1: selectedProduct.leadTime1,
            threshold2: selectedProduct.threshold2,
            leadTime2: selectedProduct.leadTime2,
            threshold3: selectedProduct.threshold3,
            leadTime3: selectedProduct.leadTime3,
            // When non-standard CSR is selected, PM will confirm lead time after order creation
            calculatedLeadTime: hasNonStandardCSR ? null : currentProduct.calculatedLeadTime
        };

        // Check if this is a $0 price product and if we need to confirm Type of Order change
        const productIsFreeSample = newProduct.unitPrice === 0 || Number(newProduct.unitPrice) === 0;
        const currentTypeIsFree = formData.typeOfOrder === 'Free Sample';
        
        if (productIsFreeSample && !currentTypeIsFree && formData.products.length > 0) {
            // Zero price product but Type of Order is not Free Sample and we have existing products
            // Show dialog to confirm changing to Free Sample
            setMixingDialog({
                isOpen: true,
                dialogType: 'zero-price',
                productToAdd: newProduct
            });
            return;
        }

        if (productIsFreeSample && !currentTypeIsFree && formData.products.length === 0) {
            // First product is $0 - automatically set to Free Sample and add it
            handleConfirmAddProduct(newProduct, true);
            return;
        }

        // Check for product mixing (Free Sample vs Paid)
        const existingMode = getExistingProductMode();
        const newMode = getNewProductMode(newProduct.unitPrice);

        if (existingMode && existingMode !== newMode) {
            // Mixing detected - show confirmation dialog
            setMixingDialog({
                isOpen: true,
                dialogType: 'mixing',
                existingMode: existingMode,
                newMode: newMode,
                productToAdd: newProduct
            });
            return;
        }

        // No issues - add product normally
        handleConfirmAddProduct(newProduct, false);
    };

    const handleRemoveProduct = (index) => {
        const removedProductCode = formData.products[index].productCode;
        setFormData(prev => ({
            ...prev,
            products: prev.products.filter((_, i) => i !== index),
            soSchedule: prev.typeOfOrder === 'Mass Order' ? prev.soSchedule.map(so => {
                const updatedPQ = { ...so.productQuantities };
                delete updatedPQ[removedProductCode];
                return { ...so, productQuantities: updatedPQ };
            }) : prev.soSchedule
        }));
    };

    // Handle CSR category toggle — also recalculates/nulls product lead times as needed
    const handleCsrCategoryChange = (option, currentlySelected) => {
        setFormData(prev => {
            const newCsrCategory = currentlySelected
                ? prev.csrCategory.filter(c => c !== option)
                : [...prev.csrCategory, option];

            const wasNonStandard = prev.csrCategory.some(cat => cat !== 'Single Packing' && cat !== 'Bulk Packing');
            const isNowNonStandard = newCsrCategory.some(cat => cat !== 'Single Packing' && cat !== 'Bulk Packing');

            let products = prev.products;
            if (prev.products.length > 0) {
                if (isNowNonStandard && !wasNonStandard) {
                    // Just became non-standard: null all product lead times (PM will confirm)
                    products = prev.products.map(p => ({ ...p, calculatedLeadTime: null }));
                } else if (!isNowNonStandard && wasNonStandard) {
                    // Just became standard: recalculate null lead times from stored catalog data
                    products = prev.products.map(p => {
                        if (p.calculatedLeadTime !== null && p.calculatedLeadTime !== undefined && p.calculatedLeadTime !== '') {
                            return p;
                        }
                        const recalculated = calculateLeadTime(parseInt(p.quantity), p);
                        return { ...p, calculatedLeadTime: (recalculated !== undefined ? recalculated : null) };
                    });
                }
            }

            return { ...prev, csrCategory: newCsrCategory, products };
        });
    };

    const getMaxLeadTime = () => {
        // Return null if custom requirements or contact-us products are selected
        if (hasNonStandardCSR || hasContactUsProduct) return null;

        const leadTimes = formData.products
            .map(p => p.calculatedLeadTime)
            .filter(lt => typeof lt === 'number' && lt !== 9999);
        return leadTimes.length > 0 ? Math.max(...leadTimes) : null;
    };

    const validateForm = () => {
        const errors = [];

        // Basic required fields validation
        if (!formData.customerName) errors.push('Customer Name is required');
        if (!formData.customerPoNumber) errors.push('Customer PO Number is required');

        // Global Address validation - SKIP FOR MASS ORDER (Mass Order uses SO-level addresses)
        if (formData.typeOfOrder !== 'Mass Order') {
            // Address validation
            if (!formData.shipToAddress.country) errors.push('Ship-To Country is required');
            if (!formData.billToAddress.country) errors.push('Bill-To Country is required');

            // India-specific validation
            if (formData.shipToAddress.country === 'India') {
                if (!formData.shipToAddress.iecNumber) errors.push('IEC Number is required for India (Ship-To)');
                if (!formData.shipToAddress.iecBranch) errors.push('IEC Branch is required for India (Ship-To)');
                if (!formData.shipToAddress.gst) errors.push('GST is required for India (Ship-To)');
                if (!formData.shipToAddress.adCode) errors.push('AD Code is required for India (Ship-To)');
            }

            if (formData.billToAddress.country === 'India') {
                if (!formData.billToAddress.iecNumber) errors.push('IEC Number is required for India (Bill-To)');
                if (!formData.billToAddress.iecBranch) errors.push('IEC Branch is required for India (Bill-To)');
                if (!formData.billToAddress.gst) errors.push('GST is required for India (Bill-To)');
                if (!formData.billToAddress.adCode) errors.push('AD Code is required for India (Bill-To)');
            }

            // European validation
            if (isEuropeanCountry(formData.shipToAddress.country)) {
                if (!formData.shipToAddress.vatId) errors.push('Ship-To VAT ID is required for European countries');
                if (!formData.shipToAddress.eoriNumber) errors.push('Ship-To EORI Number is required for European countries');
            }
            if (isEuropeanCountry(formData.billToAddress.country)) {
                if (!formData.billToAddress.vatId) errors.push('Bill-To VAT ID is required for European countries');
                if (!formData.billToAddress.eoriNumber) errors.push('Bill-To EORI Number is required for European countries');
            }
        }

        // Products validation
        if (formData.products.length === 0) {
            errors.push('At least one product is required');
        }



// Mass Order validation
if (formData.typeOfOrder === 'Mass Order') {
    if (!formData.numberOfSOs || Number(formData.numberOfSOs) < 1) {
        errors.push('Number of SOs is required for Mass Order');
    }

    if (formData.soSchedule.length !== Number(formData.numberOfSOs)) {
        errors.push('Please provide the schedule for all SOs');
    }

    // Product-wise quantity validation
    for (const product of formData.products) {
        const soQuantitiesForProduct = formData.soSchedule.map(
            so => Number(so.productQuantities?.[product.productCode]) || 0
        );
        const totalSOQuantityForProduct = soQuantitiesForProduct.reduce((sum, qty) => sum + qty, 0);
        const masterQuantityForProduct = Number(product.quantity) || 0;

        if (totalSOQuantityForProduct !== masterQuantityForProduct) {
            errors.push(
                `Product ${product.productCode}: SO quantities (${totalSOQuantityForProduct}) must equal master quantity (${masterQuantityForProduct}).`
            );
        }
    }

    formData.soSchedule.forEach((so) => {
        // Validate SO has at least one product with quantity
        const soTotal = Object.values(so.productQuantities || {}).reduce(
            (sum, qty) => sum + (Number(qty) || 0),
            0
        );

        if (!so.deliveryDate) {
            errors.push(`SO ${so.soNumber}: CRD is required`);
        }

        if (soTotal <= 0) {
            errors.push(`SO ${so.soNumber}: At least one product quantity must be specified`);
        }

        // For SO 1, validate addresses and contact (they're always shown)
        if (so.soNumber === 1) {
            if (!so.shippingAddress?.address1) {
                errors.push(`SO 1: Shipping Address 1 is required`);
            }
            if (!so.shippingAddress?.city) {
                errors.push(`SO 1: Shipping City is required`);
            }
            if (!so.shippingAddress?.country) {
                errors.push(`SO 1: Shipping Country is required`);
            }
            if (!so.shippingAddress?.zipCode) {
                errors.push(`SO 1: Shipping Zip Code is required`);
            }
            if (!so.billingAddress?.address1) {
                errors.push(`SO 1: Billing Address 1 is required`);
            }
            if (!so.billingAddress?.city) {
                errors.push(`SO 1: Billing City is required`);
            }
            if (!so.billingAddress?.country) {
                errors.push(`SO 1: Billing Country is required`);
            }
            if (!so.billingAddress?.zipCode) {
                errors.push(`SO 1: Billing Zip Code is required`);
            }
            if (!so.receiverContact?.name) {
                errors.push(`SO 1: Receiver Contact Name is required`);
            }
            if (!so.receiverContact?.number) {
                errors.push(`SO 1: Receiver Contact Number is required`);
            }
            if (!so.receiverContact?.email) {
                errors.push(`SO 1: Receiver Email is required`);
            }
        }
        // For SO 2+, validate only if "Same as SO 1" is unchecked
        else if (!so.sameAsFirst) {
            if (!so.shippingAddress?.address1) {
                errors.push(`SO ${so.soNumber}: Shipping Address 1 is required`);
            }
            if (!so.shippingAddress?.city) {
                errors.push(`SO ${so.soNumber}: Shipping City is required`);
            }
            if (!so.shippingAddress?.country) {
                errors.push(`SO ${so.soNumber}: Shipping Country is required`);
            }
            if (!so.shippingAddress?.zipCode) {
                errors.push(`SO ${so.soNumber}: Shipping Zip Code is required`);
            }
            if (!so.billingAddress?.address1) {
                errors.push(`SO ${so.soNumber}: Billing Address 1 is required`);
            }
            if (!so.billingAddress?.city) {
                errors.push(`SO ${so.soNumber}: Billing City is required`);
            }
            if (!so.billingAddress?.country) {
                errors.push(`SO ${so.soNumber}: Billing Country is required`);
            }
            if (!so.billingAddress?.zipCode) {
                errors.push(`SO ${so.soNumber}: Billing Zip Code is required`);
            }
            if (!so.receiverContact?.name) {
                errors.push(`SO ${so.soNumber}: Receiver Contact Name is required`);
            }
            if (!so.receiverContact?.number) {
                errors.push(`SO ${so.soNumber}: Receiver Contact Number is required`);
            }
            if (!so.receiverContact?.email) {
                errors.push(`SO ${so.soNumber}: Receiver Email is required`);
            }

            // Validate account mapping if checked for this SO
            // For SO2+, only validate if "Same as SO 1" is unchecked (i.e., fields are not hidden)
            if ((so.soNumber === 1 || !so.sameAsFirst) && so.mapToDifferentAccount) {
                if (!so.customerAccountEmailId) {
                    errors.push(`SO ${so.soNumber}: Customer Account Email ID is required when "Map to Different Account" is checked`);
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(so.customerAccountEmailId)) {
                    errors.push(`SO ${so.soNumber}: Customer Account Email ID must be a valid email address`);
                }
            }
        }
    });

    // Validate PO Value is required for Mass Order
    if (!formData.poValue) {
        errors.push('PO Value is required.');
    }

    // Validate PO Value matches Total Order Value for Mass Order
    if (formData.poValue) {
        const poValueNum = Number(Number(formData.poValue).toFixed(2));
        const productsSubtotal = formData.products.reduce((sum, p) => sum + (Number(p.quantity) * Number(p.unitPrice || p.price || 0)), 0);
        const totalOrderValue = Number((productsSubtotal + (Number(formData.shippingCost) || 0)).toFixed(2));
        if (poValueNum !== totalOrderValue) {
            errors.push(`PO Value ($${poValueNum.toFixed(2)}) must match the Total Order Value ($${totalOrderValue.toFixed(2)}).`);
        }
    }
}



        // Validate consistency: all products should match the Type of Order
        if (formData.typeOfOrder === 'Free Sample') {
            formData.products.forEach((product, index) => {
                if ((product.unitPrice !== 0 && Number(product.unitPrice) !== 0)) {
                    errors.push(`Product ${index + 1} (${product.productName}): Free Sample orders require $0 unit price. Found: $${product.unitPrice}`);
                }
            });
        } else {
            formData.products.forEach((product, index) => {
                if ((product.unitPrice === 0 || Number(product.unitPrice) === 0)) {
                    errors.push(`Product ${index + 1} (${product.productName}): Paid orders cannot have $0 unit price. Please correct or change order type.`);
                }
            });
        }

        // Shipping validation
        if (!hasZeroShippingCost && !formData.shippingCost) errors.push('Shipping Cost is required');
        if (formData.typeOfOrder === 'Free Sample' && Number(formData.shippingCost) !== 0) {
            errors.push('Shipping Cost must be $0 for Free Sample orders');
        }
        if (!formData.incoterms) errors.push('Incoterms is required');

        // Mobile number must be digits only
        if (formData.receiverContactNumber && !/^\d+$/.test(formData.receiverContactNumber)) {
            errors.push('Receiver Contact Number must contain digits only (no spaces or special characters)');
        }

        // Email validation
        if (formData.receiverEmailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.receiverEmailId)) {
            errors.push('Receiver Email ID must be a valid email address');
        }

        if (formData.mapToDifferentAccount) {
            if (!formData.customerAccountEmailId) {
                errors.push('Customer Account Email ID is required when "Map to Different Account" is checked');
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerAccountEmailId)) {
                errors.push('Customer Account Email ID must be a valid email address');
            }
        }

        // Validate product quantities and unit prices are integer numbers (no fractions)
        formData.products.forEach((product, index) => {
            if (!/^\d+$/.test(String(product.quantity))) {
                errors.push(`Product ${index + 1} (${product.productName}): Quantity must be a whole number`);
            }
            if (product.unitPrice !== null && product.unitPrice !== undefined && product.unitPrice !== '') {
                if (!/^\d+$/.test(String(product.unitPrice))) {
                    errors.push(`Product ${index + 1} (${product.productName}): Unit price must be a whole number (no decimals)`);
                }
            }
        });

        // Shipping account validation
        if (formData.useShippingAccount) {
            if (!String(formData.shippingAccountNumber || '').trim()) errors.push('Shipping Account Number is required');
            if (!String(formData.courierName || '').trim()) errors.push('Courier Name is required');
        }

        // Payment validation
        if (formData.paymentMode && formData.paymentMode.toLowerCase().includes('wire') && !formData.paymentReceived) {
            // Example validation if needed, or just keep basic required check
        }
        if (!formData.paymentMode) errors.push('Payment Mode is required');
        if (!formData.paymentReceived) errors.push('Payment Received status is required');
        if (!formData.purchaseLocation) errors.push('Purchase Location is required');

        // Attachment validation: mandatory if paymentReceivedDate is present (only for existing invoices with IDs)
        // For Create Mode: We check if files are SELECTED if date is present
        if (formData.paymentReceivedDate) {
            if (isEditMode && attachmentCount === 0) {
                errors.push('Attachment is mandatory when Payment Received Date is provided.');
            } else if (!isEditMode && selectedFiles.length === 0) {
                errors.push('Please select at least one file to attach since Payment Received Date is provided.');
            }
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError(null);
        setSuccess(null);

        // Ensure BD Name is set (double check)
        if (!formData.bdName && user) {
            formData.bdName = user.name;
        }

        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setError(
                <div>
                    <strong>Please fix the following errors:</strong>
                    <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                        {validationErrors.map((err, idx) => (
                            <li key={idx}>{err}</li>
                        ))}
                    </ul>
                </div>
            );
            window.scrollTo(0, 0);
            return;
        }

        // Final PO uniqueness check before submit (preflight)
        const poConflict = await checkPoAvailability(formData.customerPoNumber);
        if (poConflict) {
            setError('Customer PO Number is already used by another Sales Order. Please use a different PO Number.');
            window.scrollTo(0, 0);
            return;
        }

        try {
            setLoading(true);
            const submissionData = {
                ...formData,
                shippingCost: effectiveShippingCost
            };
            console.log('Submitting invoice data:', submissionData);

            let response;
            if (isEditMode) {
                // Update existing invoice
                response = await axios.put(`http://localhost:5000/api/invoices/${id}`, submissionData);
                setSuccess(`Sales Order updated successfully!`);
                window.scrollTo(0, 0);
                // setTimeout(() => {
                //     window.location.href = '/invoices'; // Redirect to list after edit
                // }, 2000);
            } else {
                // Create new invoice
                response = await axios.post('http://localhost:5000/api/invoices', submissionData);
                const newInvoiceId = response.data.invoiceId;

                // Handle Deferred File Uploads
                if (selectedFiles.length > 0) {
                    console.log(`sales order created (${newInvoiceId}), uploading ${selectedFiles.length} deferred files...`);
                    for (const file of selectedFiles) {
                        const fileData = new FormData();
                        fileData.append('file', file);
                        fileData.append('invoiceId', newInvoiceId);

                        await axios.post('http://localhost:5000/api/attachments/upload', fileData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });
                    }
                }

                setSuccess(`Sales Order drafted and files attached successfully! Sales Order ID: ${newInvoiceId}`);
                window.scrollTo(0, 0);
                setTimeout(() => {
                    window.location.href = '/invoices';
                }, 2000);
            }

        } catch (err) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} invoice:`, err);
            console.error('Error response:', err.response?.data);
            const errorMsg = err.response?.data?.error || err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} Sales Order. Please try again.`;
            setError(errorMsg);
            window.scrollTo(0, 0);
        } finally {
            setLoading(false);
        }
    };

    const isIndia = (addressType) => {
        return formData[addressType].country === 'India';
    };

    const isEuropean = () => {
        return isEuropeanCountry(formData.shipToAddress.country) ||
            isEuropeanCountry(formData.billToAddress.country);
    };

    const maxLeadTime = getMaxLeadTime();
    

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {isEditMode && (
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                            type="button"
                            onClick={() => window.location.href = '/invoices'}
                            style={{
                                padding: '0.5rem 1rem',
                                background: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            ← Back to List
                        </button>
                        <button
                            type="button"
                            onClick={() => window.open(`http://localhost:5000/api/invoices/${id}/pdf`, '_blank')}
                            style={{
                                padding: '0.5rem 1rem',
                                background: '#6366f1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            📥 Download PDF
                        </button>
                    </div>
                )}
                <h1 className="page-title" style={{ margin: 0 }}>
                    {isEditMode ? '✏️ Edit Sales Order' : '📄 Create Sales Order'}
                </h1>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '600' }}>
                        {isTableView ? '📊 Table View' : '🗂️ Card View'}
                    </span>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={isTableView}
                            onChange={toggleViewMode}
                        />
                        <span className="toggle-slider"></span>
                    </label>
                </div>
            </div>

            {error && <div className="card"><div className="error-message">{error}</div></div>}
            {success && <div className="card"><div className="success-message">{success}</div></div>}

            {/* Free Sample Confirmation Dialog (Mixing or Zero-Price) */}
            {mixingDialog.isOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '2rem',
                        borderRadius: '8px',
                        maxWidth: '500px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                        {mixingDialog.dialogType === 'mixing' ? (
                            <>
                                <h3 style={{ color: '#e67e22', marginTop: 0 }}>⚠️ Product Type Mismatch</h3>
                                <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                                    You can add only <strong>{mixingDialog.existingMode === 'free' ? 'Free Sample' : 'Paid'}</strong> products in this invoice.
                                    <br /><br />
                                    You already have <strong>{mixingDialog.existingMode === 'free' ? 'free sample products' : 'paid products'}</strong> added.
                                    <br /><br />
                                    You're trying to add a <strong>{mixingDialog.newMode === 'free' ? 'free sample product' : 'paid product'}</strong>.
                                </p>
                                <p style={{ backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '4px', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                    Please create a separate new Sales Order for {mixingDialog.newMode === 'free' ? 'free samples' : 'paid products'}.
                                </p>
                                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={handleCancelDialog}
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            backgroundColor: '#6b7280',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveExistingAndProceed(mixingDialog.productToAdd)}
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            backgroundColor: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}>
                                        Remove All & Proceed
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 style={{ color: '#10b981', marginTop: 0 }}>✓ Free Sample Product Detected</h3>
                                <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                                    This product has <strong>$0 unit price</strong>, which indicates a Free Sample product.
                                    <br /><br />
                                    {formData.products.length > 0 ? (
                                        <>
                                            Your current order type is set to <strong>"{formData.typeOfOrder}"</strong>.
                                            <br /><br />
                                            If you proceed, <strong>all products in this invoice will be treated as Free Sample</strong>, and you will <strong>not be able to add any paid products</strong> further in this invoice.
                                        </>
                                    ) : (
                                        <>
                                            Would you like to change the order type to <strong>"Free Sample"</strong> and proceed with adding this product?
                                        </>
                                    )}
                                </p>
                                <p style={{ backgroundColor: '#dcfce7', padding: '1rem', borderRadius: '4px', fontSize: '0.9rem', marginBottom: '1rem', color: '#166534' }}>
                                    ℹ️ If you click Cancel, you can go back and edit the unit price to a different value.
                                </p>
                                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={handleCancelDialog}
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            backgroundColor: '#6b7280',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}>
                                        Cancel - Edit Price
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleConfirmAddProduct(mixingDialog.productToAdd, true)}
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}>
                                        Yes, Add as Free Sample
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className={isTableView ? 'table-view-form' : ''}>
                <fieldset disabled={isReadOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
                {/* SONumber (highlighted) */}
                {formData.soNumber && (
                    <div style={{ marginBottom: '1rem' }}>
                        <h2 style={{ margin: 0, padding: '0.6rem 1rem', background: '#f1f5f9', borderLeft: '4px solid #667eea', color: '#1f2937' }}>
                            SO: {formData.soNumber}
                        </h2>
                    </div>
                )}
                {/* Customer Information */}
                <div className="card">
                    <div className="form-section">
                        <h2 className="form-section-title">Customer Information</h2>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Customer Name / Company Name</label>
                                <input
                                    type="text"
                                    name="customerName"
                                    value={formData.customerName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">Customer PO Number</label>
                                <input
                                    type="text"
                                    name="customerPoNumber"
                                    value={formData.customerPoNumber}
                                    onChange={handleInputChange}
                                    onBlur={() => checkPoAvailability(formData.customerPoNumber)}
                                    required
                                />
                                {poCheckMessage && (
                                    <small style={{ display: 'block', marginTop: '0.4rem', color: poExists ? '#ef4444' : '#10b981',padding:'.2rem 1rem' }}>
                                        {poChecking ? 'Checking PO...' : poCheckMessage}
                                    </small>
                                )}
                            </div>



      {/* crmOpportunityLink */}

                            <div className="form-group">
    <label>CRM Opportunity Link</label>
    <input
        type="text"
        name="crmOpportunityLink"
        value={formData.crmOpportunityLink}
        onChange={handleInputChange}
        placeholder='Enter CRM opportunity link or "New"'
    />
    {/* <small style={{ color: '#64748b', fontSize: '0.8rem' }}>
        If a new opportunity is to be created, mention "New."
    </small> */}
</div>

                            <div className="form-group">
                                <label>PI/Sales Order Reference</label>
                                <input
                                    type="text"
                                    name="piInvoiceReference"
                                    value={formData.piInvoiceReference}
                                    onChange={handleInputChange}
                                    placeholder="Enter PI / PayPal / Stripe Reference"
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">PO Value ($)</label>
                                <input
                                    type="number"
                                    name="poValue"
                                    value={formData.poValue}
                                    onChange={handleInputChange}
                                    placeholder="Enter PO value"
                                    min="0"
                                />
                            </div>
                            <div className="form-group">
                                <label>PO Date</label>
                                <input
                                    type="date"
                                    name="poDate"
                                    value={formData.poDate}
                                    onChange={handleInputChange}
                                    min="2026-01-01"
                                    max={MAX_ALLOWED_DATE}
                                />
                            </div>

 <h2 className="form-section-title">Order Type</h2>

                              <div className="form-group">
                                <label className="required">Type of Order</label>
                                <select 
                                    name="typeOfOrder" 
                                    value={formData.typeOfOrder} 
                                    onChange={handleInputChange}
                                    disabled={formData.typeOfOrder === 'Free Sample' && formData.products.length > 0}
                                    style={formData.typeOfOrder === 'Free Sample' && formData.products.length > 0 ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                                    required
                                >
                                    <option value="">Select Order Type</option>
                                    <option value="Small Order">Small Order</option>
                                    <option value="Free Sample">Free Sample</option>
                                    <option value="Mass Order">Mass Order</option>
                                    <option value="RMA">RMA</option>
                                    <option value="Service NRE">Service NRE</option>

                                </select>
                                {formData.typeOfOrder === 'Free Sample' && formData.products.length > 0 && (
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280', fontStyle: 'italic' }}>
                                        ℹ️ Type of Order is locked for Free Sample orders once products are added.
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

  {/* Product Information - MOVED HERE */}
                <div className="card">
                    <div className="form-section">
                        <h2 className="form-section-title">Product Information</h2>

                        {/* Common Configuration for All Products */}
                        <div className="common-requirements-box" style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '1.5rem',
                            marginBottom: '2rem'
                        }}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Customer Specific Requirement</label>
                                    <textarea
                                        value={formData.customerSpecificRequirement}
                                        onChange={(e) => setFormData(prev => ({ ...prev, customerSpecificRequirement: e.target.value }))}
                                        placeholder="Enter requirements in Customer Specific Requirement"
                                        rows="3"
                                    />
                                </div>
                            </div>
                            <div className="form-row product-info-divider">
                                <div className="form-group">
                                    <label>Product Status</label>
                                    <select
                                        value={formData.productStatus}
                                        onChange={(e) => setFormData(prev => ({ ...prev, productStatus: e.target.value }))}
                                        style={{borderBottom:'1px solid #d1d5db'}}
                                    >
                                        <option value="">Select Status</option>
                                        <option value="Regular">Regular</option>
                                        <option value="NPI">NPI</option>
                                                                                <option value="NPI-OTS">NPI-OTS</option>
                                        <option value="Engg-PM of PM">Engg-PM of PM</option>
                                    </select>
                                </div>

                                                                {/* Process */}
                                <div className="form-group">
    <label>Process</label>
    <select
        name="process"
        value={formData.process}
        onChange={handleInputChange}
    >
        <option value="">Select Process</option>
        <option value="ISO 9001">ISO 9001</option>
        <option value="IATF 16949">IATF 16949</option>
    </select>
</div>
                                <div className="form-group" ref={csrDropdownRef}>
                                    <label>CSR Category</label>
                                    <div style={{ position: 'relative' }}>
                                    {/* Selected Pills */}
                                    {formData.csrCategory.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.5rem 0' }}>
                                            {formData.csrCategory.map(cat => (
                                                <span
                                                    key={cat}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '0.3rem',
                                                        padding: '0.2rem 0.6rem',
                                                        background: '#764ba2',
                                                        color: 'white',
                                                        borderRadius: '20px',
                                                        fontSize: '0.82rem',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {cat}
                                                    <span
                                                        onClick={() => handleCsrCategoryChange(cat, true)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            marginLeft: '2px',
                                                            fontSize: '0.95rem',
                                                            lineHeight: '1',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        &times;
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {/* Dropdown Trigger */}
                                    <div
                                        onClick={() => setCsrDropdownOpen(prev => !prev)}
                                        style={{
                                            padding: '0.6rem 0.75rem',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            background: 'white',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            fontSize: '0.9rem',
                                            color: formData.csrCategory.length > 0 ? '#1f2937' : '#9ca3af'
                                        }}
                                    >
                                        {formData.csrCategory.length > 0
                                            ? `${formData.csrCategory.length} selected`
                                            : 'Select CSR Categories...'}
                                        <span style={{ fontSize: '0.7rem' }}>{csrDropdownOpen ? '▲' : '▼'}</span>
                                    </div>
                                    {/* Dropdown Menu */}
                                    {csrDropdownOpen && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            background: 'white',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            zIndex: 100,
                                            maxHeight: '220px',
                                            overflowY: 'auto',
                                            marginTop: '4px'
                                        }}>
                                            {CSR_CATEGORY_OPTIONS.map(option => {
                                                const isSelected = formData.csrCategory.includes(option);
                                                return (
                                                    <div
                                                        key={option}
                                                        onClick={() => handleCsrCategoryChange(option, isSelected)}
                                                        style={{
                                                            padding: '0.5rem 0.75rem',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.5rem',
                                                            background: isSelected ? '#f5f0ff' : 'white',
                                                            borderBottom: '1px solid #f1f5f9',
                                                            transition: 'background 0.15s'
                                                        }}
                                                        onMouseOver={(e) => e.currentTarget.style.background = isSelected ? '#ede5ff' : '#f8fafc'}
                                                        onMouseOut={(e) => e.currentTarget.style.background = isSelected ? '#f5f0ff' : 'white'}
                                                    >
                                                        <span style={{
                                                            width: '18px',
                                                            height: '18px',
                                                            borderRadius: '4px',
                                                            border: isSelected ? '2px solid #764ba2' : '2px solid #cbd5e1',
                                                            background: isSelected ? '#764ba2' : 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white',
                                                            fontSize: '0.7rem',
                                                            flexShrink: 0
                                                        }}>
                                                            {isSelected && '✓'}
                                                        </span>
                                                        <span style={{ fontSize: '0.9rem', color: '#374151' }}>{option}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Add New Product Form */}
                        {!isReadOnly && (
                            <>
                                <h3 style={{ marginBottom: '1rem', color: '#667eea' }}>Add Product</h3>

                                <div className="form-row">
                            <div className="form-group search-dropdown">
                                <label className="required">Product Code</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Type at least 3 characters to search..."
                                />
                                {showDropdown && searchResults.length > 0 && (
                                    <div className="search-results">
                                        {searchResults
                                            .filter(p => p.isActive)
                                            .map(product => (
                                                <div
                                                    key={product.productCode || product.id}
                                                    className="search-result-item"
                                                    onClick={() => handleProductSelect(product)}
                                                >
                                                    <div className="search-result-info" style={{ paddingLeft: 0 }}>
                                                        <div className="search-result-code">{product.productCode}</div>
                                                        <div className="search-result-name">{product.productName}</div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                                {showDropdown && searchTerm.length >= 3 && searchResults.length === 0 && !selectedProduct && (
                                    <div className="search-results">
                                        <div className="search-no-results">No products found</div>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="required">Quantity</label>
                                <input
                                    type="number"
                                    value={currentProduct.quantity}
                                    onChange={(e) => setCurrentProduct(prev => ({ ...prev, quantity: e.target.value }))}
                                    min="1"
                                />
                            </div>

                            <div className="form-group">
                                <label>Unit Price ($)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={currentProduct.unitPrice}
                                    onChange={(e) => setCurrentProduct(prev => ({ ...prev, unitPrice: e.target.value }))}
                                    placeholder="Enter unit price (optional for Free Samples)"
                                />
                            </div>

                                </div>
                            </>
                        )}


{formData.typeOfOrder !== 'Mass Order' && (

                        <div className="form-row">

                            <div className="form-group">
                                <label>Calculated Lead Time</label>
                                <input
                                    type="text"
                                    value={
                                        // Specific Logic: If any non-standard packing option is selected, lead time is Pending PM
                                        hasNonStandardCSR ? '' :
                                            (typeof currentProduct.calculatedLeadTime === 'number' && currentProduct.calculatedLeadTime !== 9999
                                                ? `${currentProduct.calculatedLeadTime} days`
                                                : '')
                                    }
                                    disabled
                                    style={{ background: '#f5f5f5' }}
                                />
                                {hasNonStandardCSR && (
                                    <small style={{ color: '#e67e22', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                                        ⚠️ Custom requirements selected. Lead time updated by PM.
                                    </small>
                                )}
                                {!hasNonStandardCSR &&
                                    selectedProduct && currentProduct.quantity && currentProduct.calculatedLeadTime === 9999 && (
                                    <small style={{ color: '#e67e22', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                                        ⚠️ This is a Contact Us product. Lead time will be updated by PM post order creation.
                                    </small>
                                )}
                                {!hasNonStandardCSR &&
                                    selectedProduct && currentProduct.quantity && currentProduct.calculatedLeadTime === 'MANUAL' && (
                                    <small style={{ color: '#e67e22', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                                        ⚠️ Please contact PM for leadtime. CRD will be updated by PM post order creation.
                                    </small>
                                )}
                                {!hasNonStandardCSR &&
                                    selectedProduct && currentProduct.quantity && currentProduct.calculatedLeadTime === 'INVALID' && (
                                    <small style={{ color: '#ef4444', fontSize: '0.8rem', display: 'block', marginTop: '4px', fontWeight: '600' }}>
                                        ❌ Unable to order this quantity. Allowed: 1-{selectedProduct.threshold1}{selectedProduct.threshold2 > 0 ? `, ${selectedProduct.threshold2}` : ''}{selectedProduct.threshold3 > 0 ? `, ${selectedProduct.threshold3}` : ''}, or &gt;{selectedProduct.threshold3 || selectedProduct.threshold2 || selectedProduct.threshold1}.
                                    </small>
                                )}
                            </div>
                        </div>
)}
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                        <button
                            type="button"
                            onClick={handleAddProduct}
                            style={{
                                padding: '0.75rem 2rem',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            + Add Product to SO
                        </button>
                        </div>
                    </div>
                </div>
                {/* Product List Table */}
                {
                    formData.products.length > 0 && (
                        <div className="card">
                            <div className="form-section">
                                <h3 className="form-section-title">Product List</h3>
                                <div className="table-container">
                                    <table className="product-table">
                                        <thead>
                                            <tr>
                                                <th>Product Code</th>
                                                <th>Description</th>
                                                <th>Qty</th>
                                                <th>Unit Price</th>
                                                <th>Subtotal</th>
                                               {formData.typeOfOrder !== 'Mass Order' && (
            <th>Lead Time</th>
        )}
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.products.map((product, index) => {
                                                const isFreeSample = formData.typeOfOrder === 'Free Sample';
                                                const displayPrice = isFreeSample ? 0 : (product.unitPrice || product.price);
                                                const displayLineTotal = isFreeSample ? 0 : (Number(product.quantity) * Number(product.unitPrice || product.price || 0));
                                                return (
                                                <tr key={index}>
                                                    <td>{product.productCode}</td>
                                                    <td>
                                                        {product.productName}
                                                        {isFreeSample && <span style={{ display: 'block', fontSize: '0.8rem', color: '#10b981', fontWeight: '600', marginTop: '0.25rem' }}>FREE SAMPLE</span>}
                                                    </td>
                                                    <td>{product.quantity}</td>
                                                    <td>${displayPrice}</td>
                                                    <td style={{ fontWeight: '600' }}>
                                                        ${displayLineTotal.toFixed(2)}
                                                    </td>

                                                    {formData.typeOfOrder !== 'Mass Order' && (  
                                                    <td>
                                                        {(hasNonStandardCSR || typeof product.calculatedLeadTime !== 'number' || product.calculatedLeadTime === 9999) ? (
                                                            <span style={{ color: '#e67e22', fontStyle: 'italic' }}>Pending PM</span>
                                                        ) : `${product.calculatedLeadTime} days`}
                                                    </td>
                                                    )}
                                                    <td>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveProduct(index)}
                                                            style={{
                                                                padding: '0.25rem 0.5rem',
                                                                background: '#ef4444',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{
                                    marginTop: '1rem',
                                    textAlign: 'right',
                                    fontSize: '1.1rem',
                                    color: '#4b5563',
                                    fontWeight: '600',
                                    borderTop: '1px solid #e5e7eb',
                                    paddingTop: '0.5rem'
                                }}>
                                    Products Subtotal:
                                    <span style={{ color: '#1f2937', marginLeft: '0.5rem' }}>
                                        ${formData.typeOfOrder === 'Free Sample' 
                                            ? '0.00' 
                                            : formData.products.reduce((sum, p) => sum + (Number(p.quantity) * Number(p.unitPrice || p.price || 0)), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        }
                                    </span>
                                </div>
                                {formData.products.some(p => !p.calculatedLeadTime) && (
                                    <div style={{
                                        marginTop: '1rem',
                                        padding: '0.75rem 1rem',
                                        background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                                        border: '1px solid #f0c040',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '0.6rem'
                                    }}>
                                        <span style={{ fontSize: '1.2rem', lineHeight: '1.4' }}>⚠️</span>
                                        <div>
                                            <strong style={{ color: '#856404', display: 'block', marginBottom: '2px' }}>
                                                Contact Us Product(s) Detected
                                            </strong>
                                            <span style={{ color: '#856404', fontSize: '0.88rem' }}>
                                                One or more products are marked as "Contact Us". Lead time and Customer Requested Delivery Date will be updated by PM after order creation.
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }

             
           {/* Shipping Information */}

                {formData.typeOfOrder === 'Mass Order' && (
                <div className="card">
                    <div className="form-section">
                        <h2 className="form-section-title">Shipping Information</h2>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Shipping Cost ($)</label>
                                <div style={{ display: 'flex' }}>
                                <input 
                                    type="number" 
                                    step="0.01" 
                                    name="shippingCost" 
                                    value={hasZeroShippingCost ? '0.00' : formData.shippingCost} 
                                    onChange={handleInputChange}
                                    readOnly={hasZeroShippingCost}
                                    style={hasZeroShippingCost ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                                    required 
                                />
                                {hasZeroShippingCost && (
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#10b981', fontStyle: 'italic' }}>
                                        {formData.typeOfOrder === 'Free Sample'
                                            ? 'Shipping cost locked at $0.00 for Free Sample orders'
                                            : 'Shipping cost locked at $0.00 because the customer shipping account will be used'}
                                    </small>
                                )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="required">Incoterms</label>
                                <select name="incoterms" value={formData.incoterms} onChange={handleInputChange} required>
                                    <option value="">Select Incoterms</option>
                                    <option value="CIP">CIP</option>
                                    <option value="FOB">FOB</option>
                                    <option value="DDP">DDP</option>
                                    <option value="EXW">EXW</option>
                                </select>
                            </div>
                        </div>
                        <div className="toggle-container" style={{ marginTop: '1.5rem' }}>
                            <label className="toggle-switch">
                                <input type="checkbox" name="useShippingAccount" checked={formData.useShippingAccount} onChange={handleInputChange} />
                                <span className="toggle-slider"></span>
                            </label>
                            <span style={{ fontWeight: '600' }}>Use Shipping Account</span>
                        </div>
                        {formData.useShippingAccount && (
                            <div className="form-row" style={{ marginTop: '1rem' }}>
                                <div className="form-group">
                                    <label className="required">Shipping Account Number</label>
                                    <input type="text" name="shippingAccountNumber" value={formData.shippingAccountNumber} onChange={handleInputChange} required />
                                </div>
                                <div className="form-group">
                                    <label className="required">Courier Name</label>
                                    <input type="text" name="courierName" value={formData.courierName} onChange={handleInputChange} required />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
)}



{/* Type of order  */}
   <div className="card">
                    <div className="form-section">
                   
  {formData.typeOfOrder === 'Mass Order' && (     <h2 className="form-section-title">Sales Order Allocation</h2>)}

                              {/* <div className="form-group">
                                <label className="required">Type of Order</label>
                                <select 
                                    name="typeOfOrder" 
                                    value={formData.typeOfOrder} 
                                    onChange={handleInputChange}
                                    disabled={formData.typeOfOrder === 'Free Sample' && formData.products.length > 0}
                                    style={formData.typeOfOrder === 'Free Sample' && formData.products.length > 0 ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                                    required
                                >
                                    <option value="">Select Order Type</option>
                                    <option value="Small Order">Small Order</option>
                                    <option value="Free Sample">Free Sample</option>
                                    <option value="Mass Order">Mass Order</option>
                                    <option value="RMA">RMA</option>
                                    <option value="Service NRE">Service NRE</option>

                                </select>
                                {formData.typeOfOrder === 'Free Sample' && formData.products.length > 0 && (
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280', fontStyle: 'italic' }}>
                                        ℹ️ Type of Order is locked for Free Sample orders once products are added.
                                    </small>
                                )}
                            </div> */}

                            

  {formData.typeOfOrder === 'Mass Order' && (
    <div className="form-group">
        <label className="required">Number of SOs</label>
        <input
            type="number"
            name="numberOfSOs"
            min="1"
            value={formData.numberOfSOs}
            onChange={handleNumberOfSOsChange}
            placeholder="Enter number of SOs"
            required
        />
    </div>
)}


{/* Mass Order - SO Details with Addresses and Contacts */}
{formData.typeOfOrder === 'Mass Order' && formData.soSchedule?.length > 0 && (
    <>
        {formData.soSchedule.map((so, index) => (
            <div key={index} className="card" style={{ marginTop: '1.5rem' }}>
                <div className="form-section">
                    <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.1rem', fontWeight: '600' }}>
                        Sale Order {so.soNumber}
                    </h3>

                    {/* Mass Order - Product Quantities Section */}
                    {formData.typeOfOrder === 'Mass Order' && (
                        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                            <h5 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '0.9rem', fontWeight: '600' }}>
                                Product Quantities for SO {so.soNumber}
                            </h5>

                            {formData.products.length === 0 ? (
                                <p style={{ color: '#9ca3af', fontSize: '0.9rem', margin: 0 }}>
                                    No products selected. Add products first.
                                </p>
                            ) : (
                                <div className="table-container" style={{ marginBottom: '1rem' }}>
                                    <table className="product-table">
                                        <thead>
                                            <tr>
                                                <th>Product Code</th>
                                                <th>Description</th>
                                                <th>Master Qty</th>
                                                <th>SO {so.soNumber} Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.products.map((product) => (
                                                <tr key={product.productCode}>
                                                    <td>{product.productCode}</td>
                                                    <td>{product.productName}</td>
                                                    <td>{product.quantity}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={so.productQuantities?.[product.productCode] ?? ''}
                                                            onChange={(e) => handleSOProductQuantityChange(index, product.productCode, e.target.value)}
                                                            placeholder="0"
                                                            style={{ width: '100%', padding: '0.4rem' }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* SO Total Quantity (Read-only) */}
                            <div style={{ padding: '0.75rem', backgroundColor: '#e0f2fe', border: '1px solid #0ea5e9', borderRadius: '4px' }}>
                                <div style={{ fontSize: '0.85rem', color: '#0369a1', fontWeight: '600' }}>
                                    SO {so.soNumber} Total Quantity: {
                                        Object.values(so.productQuantities || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0)
                                    }

     {formData.typeOfOrder === 'Mass Order' && (
    <div className="form-group" style={{ marginTop: '0.75rem' }}>
        <label>SO {so.soNumber} Shipping Charge ($)</label>
        <input
            type="number"
            min="0"
            step="0.01"
            value={so.shippingCharge ?? getMassOrderShippingCharge(index)}
            onChange={(e) =>
                handleSOShippingChargeChange(index, e.target.value)
            }
            style={{ width: '100%', padding: '0.5rem' }}
        />
    </div>
)}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Quantity and Delivery Date Row (Non-Mass Order) OR CRD Only (Mass Order) */}
                    {formData.typeOfOrder === 'Mass Order' ? (
                        <div className="form-row" style={{ marginBottom: '1.5rem' }}>
                            <div className="form-group">
                                <label className="required">Customer Requested Date (CRD)</label>
                                <input
                                    type="date"
                                    value={so.deliveryDate || ''}
                                    onChange={(e) => {
                                        const updatedSchedule = [...formData.soSchedule];
                                        updatedSchedule[index] = { ...updatedSchedule[index], deliveryDate: e.target.value };
                                        setFormData(prev => ({ ...prev, soSchedule: updatedSchedule }));
                                    }}
                                    required
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="form-row" style={{ marginBottom: '1.5rem' }}>
                            <div className="form-group">
                                <label className="required">Quantity</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={so.quantity || ''}
                                    onChange={(e) => {
                                        const updatedSchedule = [...formData.soSchedule];
                                        updatedSchedule[index] = { ...updatedSchedule[index], quantity: e.target.value };
                                        setFormData(prev => ({ ...prev, soSchedule: updatedSchedule }));
                                    }}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">Customer Requested Date (CRD)</label>
                                <input
                                    type="date"
                                    value={so.deliveryDate || ''}
                                    onChange={(e) => {
                                        const updatedSchedule = [...formData.soSchedule];
                                        updatedSchedule[index] = { ...updatedSchedule[index], deliveryDate: e.target.value };
                                        setFormData(prev => ({ ...prev, soSchedule: updatedSchedule }));
                                    }}
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {/* SO 2+ "Same as SO 1" Checkbox */}
                    {index > 0 && (
                        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #e0f2fe' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={so.sameAsFirst || false}
                                    onChange={(e) => handleSameAsFirstToggle(index, e.target.checked)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span style={{ fontWeight: '600', color: '#1f2937' }}>Same as SO 1</span>
                            </label>
                            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0.5rem 0 0 2rem' }}>
                                {so.sameAsFirst ? 'SO will use the same address and contact details as SO 1' : 'SO has its own address and contact details'}
                            </p>
                        </div>
                    )}

                    {/* Show forms only for SO 1 or when SO 2+ unchecks "Same as SO 1" */}
                    {(index === 0 || !so.sameAsFirst) && (
                        <>
                            {/* Shipping Address */}
                            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                                <h4 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '0.95rem', fontWeight: '600' }}>Shipping Address</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Person / Company Name</label>
                                        <input
                                            type="text"
                                            value={so.shippingAddress?.contactName || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'contactName', e.target.value)}
                                            placeholder="Person or company name"
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="required">Address 1</label>
                                        <input
                                            type="text"
                                            value={so.shippingAddress?.address1 || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'address1', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Address 2</label>
                                        <input
                                            type="text"
                                            value={so.shippingAddress?.address2 || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'address2', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="required">City</label>
                                        <input
                                            type="text"
                                            value={so.shippingAddress?.city || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'city', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="required">State</label>
                                        {so.shippingAddress?.country === 'India' ? (
                                            <select
                                                value={so.shippingAddress?.state || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'state', e.target.value)}
                                                required
                                            >
                                                <option value="">Select State</option>
                                                {INDIAN_STATES.map(state => (
                                                    <option key={state} value={state}>{state}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={so.shippingAddress?.state || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'state', e.target.value)}
                                                required
                                            />
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label className="required">Country</label>
                                        <select
                                            value={so.shippingAddress?.country || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'country', e.target.value)}
                                            required
                                        >
                                            <option value="">Select Country</option>
                                            {COUNTRIES.map(country => (
                                                <option key={country} value={country}>{country}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="required">Zip Code</label>
                                        <input
                                            type="text"
                                            value={so.shippingAddress?.zipCode || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'zipCode', e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                {so.shippingAddress?.country === 'India' && (
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="required">IEC Number</label>
                                            <input
                                                type="text"
                                                value={so.shippingAddress?.iecNumber || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'iecNumber', e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="required">IEC Branch</label>
                                            <input
                                                type="text"
                                                value={so.shippingAddress?.iecBranch || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'iecBranch', e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="required">GST</label>
                                            <input
                                                type="text"
                                                value={so.shippingAddress?.gst || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'gst', e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="required">AD Code</label>
                                            <input
                                                type="text"
                                                value={so.shippingAddress?.adCode || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'adCode', e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                {isEuropeanCountry(so.shippingAddress?.country) && (
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="required">VAT ID</label>
                                            <input
                                                type="text"
                                                value={so.shippingAddress?.vatId || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'vatId', e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="required">EORI Number</label>
                                            <input
                                                type="text"
                                                value={so.shippingAddress?.eoriNumber || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'shippingAddress', 'eoriNumber', e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Billing Address */}
                            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                                <h4 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '0.95rem', fontWeight: '600' }}>Billing Address</h4>

                                {/* Same as Shipping Address checkbox */}
                                <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #e0f2fe' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={soBillingSameAsShipping[index] || false}
                                            onChange={(e) => handleSOBillingSameAsShipping(index, e.target.checked)}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                        <span style={{ fontWeight: '600', color: '#1f2937' }}>Same as Shipping Address</span>
                                    </label>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Person / Company Name</label>
                                        <input
                                            type="text"
                                            value={so.billingAddress?.contactName || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'contactName', e.target.value)}
                                            placeholder="Person or company name"
                                            disabled={soBillingSameAsShipping[index] || false}
                                            style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="required">Address 1</label>
                                        <input
                                            type="text"
                                            value={so.billingAddress?.address1 || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'address1', e.target.value)}
                                            disabled={soBillingSameAsShipping[index] || false}
                                            style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Address 2</label>
                                        <input
                                            type="text"
                                            value={so.billingAddress?.address2 || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'address2', e.target.value)}
                                            disabled={soBillingSameAsShipping[index] || false}
                                            style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="required">City</label>
                                        <input
                                            type="text"
                                            value={so.billingAddress?.city || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'city', e.target.value)}
                                            disabled={soBillingSameAsShipping[index] || false}
                                            style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="required">State</label>
                                        {so.billingAddress?.country === 'India' ? (
                                            <select
                                                value={so.billingAddress?.state || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'state', e.target.value)}
                                                disabled={soBillingSameAsShipping[index] || false}
                                                style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                                required
                                            >
                                                <option value="">Select State</option>
                                                {INDIAN_STATES.map(state => (
                                                    <option key={state} value={state}>{state}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={so.billingAddress?.state || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'state', e.target.value)}
                                                disabled={soBillingSameAsShipping[index] || false}
                                                style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                                required
                                            />
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label className="required">Country</label>
                                        <select
                                            value={so.billingAddress?.country || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'country', e.target.value)}
                                            disabled={soBillingSameAsShipping[index] || false}
                                            style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                            required
                                        >
                                            <option value="">Select Country</option>
                                            {COUNTRIES.map(country => (
                                                <option key={country} value={country}>{country}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="required">Zip Code</label>
                                        <input
                                            type="text"
                                            value={so.billingAddress?.zipCode || ''}
                                            onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'zipCode', e.target.value)}
                                            disabled={soBillingSameAsShipping[index] || false}
                                            style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                            required
                                        />
                                    </div>
                                </div>

                                {so.billingAddress?.country === 'India' && (
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="required">IEC Number</label>
                                            <input
                                                type="text"
                                                value={so.billingAddress?.iecNumber || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'iecNumber', e.target.value)}
                                                disabled={soBillingSameAsShipping[index] || false}
                                                style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="required">IEC Branch</label>
                                            <input
                                                type="text"
                                                value={so.billingAddress?.iecBranch || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'iecBranch', e.target.value)}
                                                disabled={soBillingSameAsShipping[index] || false}
                                                style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="required">GST</label>
                                            <input
                                                type="text"
                                                value={so.billingAddress?.gst || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'gst', e.target.value)}
                                                disabled={soBillingSameAsShipping[index] || false}
                                                style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="required">AD Code</label>
                                            <input
                                                type="text"
                                                value={so.billingAddress?.adCode || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'adCode', e.target.value)}
                                                disabled={soBillingSameAsShipping[index] || false}
                                                style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                {isEuropeanCountry(so.billingAddress?.country) && (
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="required">VAT ID</label>
                                            <input
                                                type="text"
                                                value={so.billingAddress?.vatId || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'vatId', e.target.value)}
                                                disabled={soBillingSameAsShipping[index] || false}
                                                style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="required">EORI Number</label>
                                            <input
                                                type="text"
                                                value={so.billingAddress?.eoriNumber || ''}
                                                onChange={(e) => handleSOAddressChange(index, 'billingAddress', 'eoriNumber', e.target.value)}
                                                disabled={soBillingSameAsShipping[index] || false}
                                                style={soBillingSameAsShipping[index] ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Receiver Contact */}
                            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                                <h4 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '0.95rem', fontWeight: '600' }}>Receiver Contact</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="required">Receiver Contact Name</label>
                                        <input
                                            type="text"
                                            value={so.receiverContact?.name || ''}
                                            onChange={(e) => handleSOReceiverContactChange(index, 'name', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="required">Receiver Contact Number</label>
                                        <input
                                            type="tel"
                                            value={so.receiverContact?.number || ''}
                                            onChange={(e) => handleSOReceiverContactChange(index, 'number', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="required">Receiver Email</label>
                                        <input
                                            type="email"
                                            value={so.receiverContact?.email || ''}
                                            onChange={(e) => handleSOReceiverContactChange(index, 'email', e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Account Mapping */}
                            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                                <h4 style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '0.95rem', fontWeight: '600' }}>Account Mapping</h4>
                                <div className="form-group">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.4rem' }}>
                                        <input
                                            type="checkbox"
                                            checked={so.mapToDifferentAccount || false}
                                            onChange={(e) => handleSOAccountMappingChange(index, 'mapToDifferentAccount', e.target.checked)}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        <span style={{ fontWeight: '600', color: '#667eea' }}>Map to Different Account</span>
                                    </label>
                                    {so.mapToDifferentAccount && (
                                        <>
                                            
                                                <label ></label>
<label className="required">Customer Account Email ID</label>
                                            <input
                                                type="email"
                                                value={so.customerAccountEmailId || ''}
                                                onChange={(e) => handleSOAccountMappingChange(index, 'customerAccountEmailId', e.target.value)}
                                                required
                                                placeholder="Enter customer account email"
                                            />

                                            
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        ))}
    </>
)}
                        </div>
                        </div>


                {/* Ship-To Address - Hidden for Mass Order */}
                {formData.typeOfOrder !== 'Mass Order' && (
                <div className="card">
                    <div className="form-section">
                        <h2 className="form-section-title">Ship-To Address</h2>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Person / Company Name</label>
                                <input
                                    type="text"
                                    value={formData.shipToAddress.contactName}
                                    onChange={(e) => handleAddressChange('shipToAddress', 'contactName', e.target.value)}
                                    placeholder="Person or company name"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Address 1</label>
                                <input
                                    type="text"
                                    value={formData.shipToAddress.address1}
                                    onChange={(e) => handleAddressChange('shipToAddress', 'address1', e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Address 2</label>
                                <input
                                    type="text"
                                    value={formData.shipToAddress.address2}
                                    onChange={(e) => handleAddressChange('shipToAddress', 'address2', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">City</label>
                                <input
                                    type="text"
                                    value={formData.shipToAddress.city}
                                    onChange={(e) => handleAddressChange('shipToAddress', 'city', e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">State</label>
                                {formData.shipToAddress.country === 'India' ? (
                                    <select
                                        value={formData.shipToAddress.state}
                                        onChange={(e) => handleAddressChange('shipToAddress', 'state', e.target.value)}
                                        required
                                    >
                                        <option value="">Select State</option>
                                        {INDIAN_STATES.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={formData.shipToAddress.state}
                                        onChange={(e) => handleAddressChange('shipToAddress', 'state', e.target.value)}
                                        required
                                    />
                                )}
                            </div>
                            <div className="form-group">
                                <label className="required">Country</label>
                                <select
                                    value={formData.shipToAddress.country}
                                    onChange={(e) => handleAddressChange('shipToAddress', 'country', e.target.value)}
                                    required
                                >
                                    <option value="">Select Country</option>
                                    {COUNTRIES.map(country => (
                                        <option key={country} value={country}>{country}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="required">Zip Code</label>
                                <input
                                    type="text"
                                    value={formData.shipToAddress.zipCode}
                                    onChange={(e) => handleAddressChange('shipToAddress', 'zipCode', e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        {isIndia('shipToAddress') && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="required">IEC Number</label>
                                    <input
                                        type="text"
                                        value={formData.shipToAddress.iecNumber}
                                        onChange={(e) => handleAddressChange('shipToAddress', 'iecNumber', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="required">IEC Branch</label>
                                    <input
                                        type="text"
                                        value={formData.shipToAddress.iecBranch}
                                        onChange={(e) => handleAddressChange('shipToAddress', 'iecBranch', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="required">GST</label>
                                    <input
                                        type="text"
                                        value={formData.shipToAddress.gst}
                                        onChange={(e) => handleAddressChange('shipToAddress', 'gst', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="required">AD Code</label>
                                    <input
                                        type="text"
                                        value={formData.shipToAddress.adCode}
                                        onChange={(e) => handleAddressChange('shipToAddress', 'adCode', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {isEuropeanCountry(formData.shipToAddress.country) && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="required">VAT ID</label>
                                    <input
                                        type="text"
                                        value={formData.shipToAddress.vatId}
                                        onChange={(e) => handleAddressChange('shipToAddress', 'vatId', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="required">EORI Number</label>
                                    <input
                                        type="text"
                                        value={formData.shipToAddress.eoriNumber}
                                        onChange={(e) => handleAddressChange('shipToAddress', 'eoriNumber', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* Bill-To Address - Hidden for Mass Order */}
                {formData.typeOfOrder !== 'Mass Order' && (
                <div className="card">
                    <div className="form-section">
                        <h2 className="form-section-title">Bill-To Address</h2>

                        <div style={{ marginBottom: '0rem',padding:'1rem',borderBottom:'1px solid #e5e7eb' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={billingSameAsShipping}
                                    onChange={(e) => handleBillingSameAsShipping(e.target.checked)}
                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                />
                                <span style={{ fontWeight: '600', color: '#667eea' }}>
                                    Same as Shipping Address
                                </span>
                            </label>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Person / Company Name</label>
                                <input
                                    type="text"
                                    value={formData.billToAddress.contactName}
                                    onChange={(e) => handleAddressChange('billToAddress', 'contactName', e.target.value)}
                                    disabled={billingSameAsShipping}
                                    placeholder="Person or company name"
                                    style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Address 1</label>
                                <input
                                    type="text"
                                    value={formData.billToAddress.address1}
                                    onChange={(e) => handleAddressChange('billToAddress', 'address1', e.target.value)}
                                    disabled={billingSameAsShipping}
                                    required
                                    style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                />
                            </div>
                            <div className="form-group">
                                <label>Address 2</label>
                                <input
                                    type="text"
                                    value={formData.billToAddress.address2}
                                    onChange={(e) => handleAddressChange('billToAddress', 'address2', e.target.value)}
                                    disabled={billingSameAsShipping}
                                    style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">City</label>
                                <input
                                    type="text"
                                    value={formData.billToAddress.city}
                                    onChange={(e) => handleAddressChange('billToAddress', 'city', e.target.value)}
                                    disabled={billingSameAsShipping}
                                    required
                                    style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                />
                            </div>
                            <div className="form-group">
                                <label className="required">State</label>
                                {formData.billToAddress.country === 'India' ? (
                                    <select
                                        value={formData.billToAddress.state}
                                        onChange={(e) => handleAddressChange('billToAddress', 'state', e.target.value)}
                                        disabled={billingSameAsShipping}
                                        required
                                        style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    >
                                        <option value="">Select State</option>
                                        {INDIAN_STATES.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={formData.billToAddress.state}
                                        onChange={(e) => handleAddressChange('billToAddress', 'state', e.target.value)}
                                        disabled={billingSameAsShipping}
                                        required
                                        style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    />
                                )}
                            </div>
                            <div className="form-group">
                                <label className="required">Country</label>
                                <select
                                    value={formData.billToAddress.country}
                                    onChange={(e) => handleAddressChange('billToAddress', 'country', e.target.value)}
                                    disabled={billingSameAsShipping}
                                    required
                                    style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                >
                                    <option value="">Select Country</option>
                                    {COUNTRIES.map(country => (
                                        <option key={country} value={country}>{country}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="required">Zip Code</label>
                                <input
                                    type="text"
                                    value={formData.billToAddress.zipCode}
                                    onChange={(e) => handleAddressChange('billToAddress', 'zipCode', e.target.value)}
                                    disabled={billingSameAsShipping}
                                    required
                                    style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                />
                            </div>
                        </div>

                        {isIndia('billToAddress') && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="required">IEC Number</label>
                                    <input
                                        type="text"
                                        value={formData.billToAddress.iecNumber}
                                        onChange={(e) => handleAddressChange('billToAddress', 'iecNumber', e.target.value)}
                                        disabled={billingSameAsShipping}
                                        required
                                        style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="required">IEC Branch</label>
                                    <input
                                        type="text"
                                        value={formData.billToAddress.iecBranch}
                                        onChange={(e) => handleAddressChange('billToAddress', 'iecBranch', e.target.value)}
                                        disabled={billingSameAsShipping}
                                        required
                                        style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="required">GST</label>
                                    <input
                                        type="text"
                                        value={formData.billToAddress.gst}
                                        onChange={(e) => handleAddressChange('billToAddress', 'gst', e.target.value)}
                                        disabled={billingSameAsShipping}
                                        required
                                        style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="required">AD Code</label>
                                    <input
                                        type="text"
                                        value={formData.billToAddress.adCode}
                                        onChange={(e) => handleAddressChange('billToAddress', 'adCode', e.target.value)}
                                        disabled={billingSameAsShipping}
                                        required
                                        style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    />
                                </div>
                            </div>
                        )}

                        {isEuropeanCountry(formData.billToAddress.country) && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="required">VAT ID</label>
                                    <input
                                        type="text"
                                        value={formData.billToAddress.vatId}
                                        onChange={(e) => handleAddressChange('billToAddress', 'vatId', e.target.value)}
                                        disabled={billingSameAsShipping}
                                        required
                                        style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="required">EORI Number</label>
                                    <input
                                        type="text"
                                        value={formData.billToAddress.eoriNumber}
                                        onChange={(e) => handleAddressChange('billToAddress', 'eoriNumber', e.target.value)}
                                        disabled={billingSameAsShipping}
                                        required
                                        style={billingSameAsShipping ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                )}

                 {/* Contact & Delivery Information - Hidden for Mass Order */}
                {formData.typeOfOrder !== 'Mass Order' && (
                <div className="card">
                    <div className="form-section">
                        <h2 className="form-section-title">Contact & Delivery Information</h2>
                            <div className="form-group">
                                <label className="required">Receiver Contact Name</label>
                                <input type="text" name="receiverContactName" value={formData.receiverContactName} onChange={handleInputChange} required />
                            </div>
                            <div className="form-group">
                                <label className="required">Receiver Contact Number</label>
                                <input type="tel" name="receiverContactNumber" value={formData.receiverContactNumber} onChange={handleInputChange} required />
                            </div>
                            <div className="form-group">
                                <label className="required">Receiver Email ID</label>
                                <input type="email" name="receiverEmailId" value={formData.receiverEmailId} onChange={handleInputChange} required />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.4rem' }}>
                                    <input
                                        type="checkbox"
                                        name="mapToDifferentAccount"
                                        checked={formData.mapToDifferentAccount}
                                        onChange={handleInputChange}
                                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                    />
                                    <span style={{ fontWeight: '600', color: '#667eea' }}>Map to Different Account</span>
                                </label>
                                {formData.mapToDifferentAccount && (
                                    
                                    <>
                                                                            <label ></label>

                                        <label className="required">Customer Account Email ID</label>
                                        <input
                                            type="email"
                                            name="customerAccountEmailId"
                                            value={formData.customerAccountEmailId}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter customer account email"
                                        />
                                    </>
                                )}
                            </div>
                        {(formData.customerRequestedDeliveryDate === 'Pending PM' || hasContactUsProduct) && (
                            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404' }}>
                                <span style={{ fontWeight: '600' }}>⏳ Pending PM Approval</span>
                                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                                    {hasContactUsProduct
                                        ? 'One or more products are Contact Us items. PM will confirm lead time and delivery date after Sales Order creation.'
                                        : 'One or more products are pending PM approval for lead time. PM will confirm delivery date after Sales Order creation.'}
                                </p>
                            </div>
                        )}
                        {formData.products.length > 0 && formData.customerRequestedDeliveryDate !== 'Pending PM' && maxLeadTime !== null && !hasNonStandardCSR && !hasContactUsProduct && (
                            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '4px', color: '#27ae60' }}>
                                <span style={{ fontWeight: '600' }}>✅ Max Lead Time Ready</span>
                                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                                    Maximum lead time: <strong>{maxLeadTime} days</strong>. PM will confirm final delivery date after order review.
                                </p>
                            </div>
                        )}
                        {/* India minimum lead time notice: show when ship-to is India and maxLeadTime < 14 */}
                        {formData.shipToAddress.country === 'India' && maxLeadTime !== null && maxLeadTime < 14 && !hasNonStandardCSR && !hasContactUsProduct && (
                            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#fff3cd', border: '1px solid #f0c040', borderRadius: '4px', color: '#856404' }}>
                                <strong>⚠️ India minimum lead time</strong>
                                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                                    For shipments to India the minimum lead time is <strong>14 days</strong>. The current selected products have a maximum lead time of <strong>{maxLeadTime} days</strong> — the system will set lead time to <strong>14 days</strong> on save.
                                </p>
                            </div>
                        )}
                        {hasNonStandardCSR && (
                            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#fff8e1', border: '1px solid #ffeb3b', borderRadius: '4px', color: '#856404' }}>
                                <span style={{ fontWeight: '600' }}>⚠️ Custom Requirements</span>
                                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                                    Custom requirements selected. PM will confirm delivery date post order creation.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                )}
                {/* Shipping Information */}

                {formData.typeOfOrder !== 'Mass Order' && (
                <div className="card">
                    <div className="form-section">
                        <h2 className="form-section-title">Shipping Information</h2>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Shipping Cost ($)</label>
                                <div style={{ display: 'flex' }}>
                                <input 
                                    type="number" 
                                    step="0.01" 
                                    name="shippingCost" 
                                    value={hasZeroShippingCost ? '0.00' : formData.shippingCost} 
                                    onChange={handleInputChange}
                                    readOnly={hasZeroShippingCost}
                                    style={hasZeroShippingCost ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                                    required 
                                />
                                {hasZeroShippingCost && (
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#10b981', fontStyle: 'italic' }}>
                                        {formData.typeOfOrder === 'Free Sample'
                                            ? 'Shipping cost locked at $0.00 for Free Sample orders'
                                            : 'Shipping cost locked at $0.00 because the customer shipping account will be used'}
                                    </small>
                                )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="required">Incoterms</label>
                                <select name="incoterms" value={formData.incoterms} onChange={handleInputChange} required>
                                    <option value="">Select Incoterms</option>
                                    <option value="CIP">CIP</option>
                                    <option value="FOB">FOB</option>
                                    <option value="DDP">DDP</option>
                                    <option value="EXW">EXW</option>
                                </select>
                            </div>
                        </div>
                        <div className="toggle-container" style={{ marginTop: '1.5rem' }}>
                            <label className="toggle-switch">
                                <input type="checkbox" name="useShippingAccount" checked={formData.useShippingAccount} onChange={handleInputChange} />
                                <span className="toggle-slider"></span>
                            </label>
                            <span style={{ fontWeight: '600' }}>Use Shipping Account</span>
                        </div>
                        {formData.useShippingAccount && (
                            <div className="form-row" style={{ marginTop: '1rem' }}>
                                <div className="form-group">
                                    <label className="required">Shipping Account Number</label>
                                    <input type="text" name="shippingAccountNumber" value={formData.shippingAccountNumber} onChange={handleInputChange} required />
                                </div>
                                <div className="form-group">
                                    <label className="required">Courier Name</label>
                                    <input type="text" name="courierName" value={formData.courierName} onChange={handleInputChange} required />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
)}
                {/* Management Information */}
                <div className="card">
                    <div className="form-section">
                        <h2 className="form-section-title">Management Information</h2>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="required">Product Manager Name</label>
                                <select name="productManagerName" value={formData.productManagerName} onChange={handleInputChange} required>
                                    <option value="">Select Product Manager</option>
                                    {PRODUCT_MANAGER_OPTIONS.map(pm => (
                                        <option key={pm} value={pm}>{pm}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="required">Purchase Location</label>
                                <select name="purchaseLocation" value={formData.purchaseLocation} onChange={handleInputChange} required>
                                    <option value="">Select Location</option>
                                    {PURCHASE_LOCATION_OPTIONS.map(loc => (
                                        <option key={loc} value={loc}>{loc}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="required">Payment Mode</label>
                                <select name="paymentMode" value={formData.paymentMode} onChange={handleInputChange} required>
                                    <option value="">Select Payment Mode</option>
                                    {PAYMENT_MODE_OPTIONS.map(mode => (
                                        <option key={mode} value={mode}>{mode}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="required">Payment Received</label>
                                <select name="paymentReceived" value={formData.paymentReceived} onChange={handleInputChange} required>
                                    <option value="">Select Payment Status</option>
                                    {PAYMENT_RECEIVED_OPTIONS.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className={formData.paymentReceived.toLowerCase().includes('advance') ? 'required' : ''}>Payment Received Date</label>
                                <input type="date" name="paymentReceivedDate" value={formData.paymentReceivedDate} onChange={handleInputChange} min="2026-01-01" max={MAX_ALLOWED_DATE} required={formData.paymentReceived.toLowerCase().includes('advance')} />
                            </div>
                            <div className="form-group" style={{ display: 'none' }}>
                                <label>BD Name</label>
                                <input type="text" name="bdName" value={formData.bdName} readOnly />
                            </div>
                          








                        </div>
                    </div>
                </div>

                {/* File Attachments Section */}
                <div className="card">
                    <div className="form-section">
                        <FileUpload
                            invoiceId={id}
                            createdBy={formData.createdBy}
                            status={formData?.status}
                            isCreateMode={!isEditMode}
                            onAttachmentsChange={(count) => setAttachmentCount(count)}
                            onFilesSelected={(files) => setSelectedFiles(files)}
                        />
                        <div style={{
                            marginTop: '1rem',
                            padding: '0.75rem 1rem',
                            background: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '8px',
                            color: '#856404',
                            fontSize: '0.9rem'
                        }}>
                            📎 <strong>Note:</strong> For wire transfer, please attach PI. For PayPal or Stripe, please attach invoice copy.
                        </div>
                    </div>
                </div>

{/* created by email  */}

{/* <div className="form-group">
    <label>Created By :</label>
    <span>{formData.createdBy?.email || '-'}</span>
</div> */}

<div
    className="form-group"
    style={{
        margin: '20px 0 28px',
        padding: '20px 24px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        boxSizing: 'border-box'
    }}
>
    <label
        style={{
            display: 'block',
            marginBottom: '8px',
            color: '#64748b',
            fontSize: '13px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        }}
    >
        Created By
    </label>

    <span
        style={{
            display: 'block',
            color: '#1e293b',
            fontSize: '14px',
            fontWeight: '500',
            paddingLeft:"10px",
            overflowWrap: 'anywhere',
            lineHeight: '1.6'
        }}
    >
        {formData.createdBy?.email || 'Not Available'}
    </span>
</div>

<PriceBreakdown formData={formData} />
                {/* Workflow Actions (if editing existing invoice) */}
                {
                    isEditMode && formData.status && (
                        <div className="card">
                            <div className="form-section" style={{ marginTop: '18px'}}>
                                <h2 className="form-section-title">Workflow Actions</h2>
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div style={{
                                        padding: '0.75rem 1.25rem',
                                        background: formData.status === 'draft' ? '#fef3c7' : formData.status === 'pending-review' ? '#ddd6fe' : '#d1fae5',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        fontSize: '0.95rem'
                                    }}>
                                        📌 Status: {formData.status?.replace(/-/g, ' ').toUpperCase()}
                                    </div>
                                    {formData.status === 'draft' && (hasRole('admin') || hasRole('normal-user') || hasRole('invoice-editor')) && (
                                        <button
                                            type="button"
                                            disabled={workflowLoading}
                                            onClick={async () => {
                                                try {
                                                    setWorkflowLoading(true);
                                                    await axios.post(`http://localhost:5000/api/workflow/invoices/${id}/submit`);
                                                    alert('Sales order submitted for review!');
                                                    window.location.reload();
                                                } catch (err) {
                                                    alert(err.response?.data?.error || 'Failed to submit');
                                                    setWorkflowLoading(false);
                                                }
                                            }}
                                            className={workflowLoading ? 'btn-loading' : ''}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                background: workflowLoading ? '#9ca3af' : '#10b981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: workflowLoading ? 'not-allowed' : 'pointer',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            {workflowLoading ? (
                                                <><div className="loading-spinner spinner-small"></div> Processing...</>
                                            ) : (
                                                <>✅ Submit for Review</>
                                            )}
                                        </button>
                                    )}
                                    {formData.status === 'pending-review' && (hasRole('admin') || hasRole('invoice-editor')) && (
                                        <button
                                            type="button"
                                            disabled={workflowLoading}
                                            onClick={async () => {
                                                try {
                                                    setWorkflowLoading(true);
                                                    await axios.post(`http://localhost:5000/api/workflow/invoices/${id}/create-so`);
                                                    alert('Sales Order Initiated!');
                                                    window.location.reload();
                                                } catch (err) {
                                                    alert(err.response?.data?.error || 'Failed to create SO');
                                                    setWorkflowLoading(false);
                                                }
                                            }}
                                            className={workflowLoading ? 'btn-loading' : ''}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                background: workflowLoading ? '#9ca3af' : '#8b5cf6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: workflowLoading ? 'not-allowed' : 'pointer',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            {workflowLoading ? (
                                                <><div className="loading-spinner spinner-small"></div> Drafting Sales Order...</>
                                            ) : (
                                                <>📋 Initiate SO Creation</>
                                            )}
                                        </button>



                                    )}

                                        {hasRole('admin') && (
                <button
                    type="button"
                   onClick={() => setShowFeedbackModal(true)}
                    style={{
                        padding: '0.75rem 1.5rem',
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600'
                    }}
                >
                    📋 Feedback
                </button>
            )}
                                    {formData.status === 'completed' && (
                                        <div style={{ padding: '0.75rem 1.25rem', background: '#d1fae5', borderRadius: '8px', fontWeight: '600', color: '#065f46' }}>
                                            ✅ SO Creation Initiated
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Invoice Total Summary */}
                <div className="card" style={{ background: '#f8fafc', border: '2px solid #e2e8f0', textAlign: 'right', padding: '1.5rem' }}>
                    <div style={{ fontSize: '1.2rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        Subtotal (Products):
                        <span style={{ fontWeight: '600', color: '#1f2937', marginLeft: '1rem' }}>
                            ${formData.typeOfOrder === 'Free Sample' 
                                ? '0.00' 
                                : formData.products.reduce((sum, p) => sum + (Number(p.quantity) * Number(p.unitPrice || p.price || 0)), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            }
                        </span>
                    </div>
                    <div style={{ fontSize: '1.2rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        Shipping Cost:
                        <span style={{ fontWeight: '600', color: '#1f2937', marginLeft: '1rem' }}>
                            ${formData.typeOfOrder === 'Free Sample' 
                                ? '0.00' 
                                : effectiveShippingCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            }
                        </span>
                    </div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: formData.typeOfOrder === 'Free Sample' ? '#10b981' : '#667eea', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                        Total Sales Order Amount:
                        <span style={{ marginLeft: '2rem' }}>
                            ${formData.typeOfOrder === 'Free Sample' 
                                ? '0.00' 
                                : (formData.products.reduce((sum, p) => sum + (Number(p.quantity) * Number(p.unitPrice || p.price || 0)), 0) + effectiveShippingCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            }
                        </span>
                    </div>
                    {formData.typeOfOrder === 'Free Sample' && (
                        <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '4px', color: '#065f46', fontSize: '0.9rem' }}>
                            ✓ This is a Free Sample order with $0 Sales Order amount
                        </div>
                    )}
                </div>

                {/* Submit Button */}
                {
                    formData?.status !== 'completed' && (
                        <div className="card">
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button
                                type="submit"
                                disabled={loading}
                                className={loading ? 'btn-loading' : ''}
                                style={{
                                    padding: '0.85rem 3rem',
                                    background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1.05rem',
                                    fontWeight: 'bold',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'transform 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.75rem'
                                }}
                                onMouseOver={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                {loading && <div className="loading-spinner spinner-small" style={{ margin: 0 }}></div>}
                                {loading ? (isEditMode ? 'Updating Sales Order...' : 'Drafting Sales Order...') : (isEditMode ? 'Update Sales Order' : 'Draft Sales Order')}
                            </button>
                            </div>
                        </div>
                    )
                }
                </fieldset>
            </form >
   

{/* Feedback Email */}
{showFeedbackModal && (
    <FeedbackModal
        loading={feedbackLoading}
        onClose={() => setShowFeedbackModal(false)}
        toEmail={formData.createdBy?.email || ''}
        onSend={async ({ to, subject, message }) => {
            try {
                setFeedbackLoading(true);

                await axios.post('http://localhost:5000/api/feedback-email', {
                    to,
                    subject,
                    message
                });

                setShowFeedbackModal(false);
                alert('Feedback sent successfully!');
            } catch (err) {
                console.error('Feedback email error:', err);
                alert(
                    err.response?.data?.error ||
                    'Failed to send feedback. Please try again.'
                );
            } finally {
                setFeedbackLoading(false);
            }
        }}
    />
)}

        </div >
    );
}

export default InvoicePage;


